/**
 * The parameter card's prose, per registry key (bata-ui#166 W2-6, DEC-09(a), DEC-52, `docs/adr/2007`).
 *
 * The platform publishes each key's STRUCTURED card on `GET /registry/parameter-keys` — unit, range,
 * vocabulary, the ADR 6038 drivers and job stages it moves, `plannerTunable`, `businessOwnerRole`,
 * `contentState` — and leaves the prose to the UI, under
 * `registry.parameterKey.<key with every "." replaced by "_">.{label,definition,formula}` in three
 * locales. The English is bata-platform's `docs/parameters/*.md` ("draft — to be signed by Bata
 * merchandising"), translated faithfully; while the platform says `contentState: drafted`, the card
 * says so. A key with no entry here has no prose, and the card says that too — never a text made up.
 *
 * One literal per key, so the catalogue check (`npm run i18n:coverage`) sees every text as used.
 *
 * **In `@/planning`** (usability audit residuals, DEC-111): only the register's pages could name a
 * parameter; in-season (what a run read, the cycle band's missing keys) showed keys. A parameter's
 * name is the platform's vocabulary, not one chapter's, so it lives where every chapter reads it.
 * An exception rule's governed key (`exception.rule.<rule id>.<field>`, bata-platform ADR 9101) is
 * named by its rule and its field.
 */

export interface ParameterTextKeys {
  readonly label: string;
  readonly definition: string;
  readonly formula: string;
}

export const PARAMETER_TEXTS: Readonly<Record<string, ParameterTextKeys>> = {
  'accuracy_bias_share_tolerance': {
    label: 'registry.parameterKey.accuracy_bias_share_tolerance.label',
    definition: 'registry.parameterKey.accuracy_bias_share_tolerance.definition',
    formula: 'registry.parameterKey.accuracy_bias_share_tolerance.formula',
  },
  // RETIRED 2026-10-21 (bata-platform ADR 8019): split into `accuracy_tracking_signal_tolerance` and
  // `accuracy_bias_share_tolerance`, and no longer declared. Kept so an older run that names it still
  // reads in words.
  'accuracy_bias_tolerance': {
    label: 'registry.parameterKey.accuracy_bias_tolerance.label',
    definition: 'registry.parameterKey.accuracy_bias_tolerance.definition',
    formula: 'registry.parameterKey.accuracy_bias_tolerance.formula',
  },
  'accuracy_minimum_observations': {
    label: 'registry.parameterKey.accuracy_minimum_observations.label',
    definition: 'registry.parameterKey.accuracy_minimum_observations.definition',
    formula: 'registry.parameterKey.accuracy_minimum_observations.formula',
  },
  'accuracy_tracking_signal_tolerance': {
    label: 'registry.parameterKey.accuracy_tracking_signal_tolerance.label',
    definition: 'registry.parameterKey.accuracy_tracking_signal_tolerance.definition',
    formula: 'registry.parameterKey.accuracy_tracking_signal_tolerance.formula',
  },
  'allocation.core_size_coverage': {
    label: 'registry.parameterKey.allocation_core_size_coverage.label',
    definition: 'registry.parameterKey.allocation_core_size_coverage.definition',
    formula: 'registry.parameterKey.allocation_core_size_coverage.formula',
  },
  'allocation.door_capacity_check_enabled': {
    label: 'registry.parameterKey.allocation_door_capacity_check_enabled.label',
    definition: 'registry.parameterKey.allocation_door_capacity_check_enabled.definition',
    formula: 'registry.parameterKey.allocation_door_capacity_check_enabled.formula',
  },
  'allocation.maximum_units_per_cell': {
    label: 'registry.parameterKey.allocation_maximum_units_per_cell.label',
    definition: 'registry.parameterKey.allocation_maximum_units_per_cell.definition',
    formula: 'registry.parameterKey.allocation_maximum_units_per_cell.formula',
  },
  'allocation.minimum_transport_units': {
    label: 'registry.parameterKey.allocation_minimum_transport_units.label',
    definition: 'registry.parameterKey.allocation_minimum_transport_units.definition',
    formula: 'registry.parameterKey.allocation_minimum_transport_units.formula',
  },
  'allocation.solve.backend_id': {
    label: 'registry.parameterKey.allocation_solve_backend_id.label',
    definition: 'registry.parameterKey.allocation_solve_backend_id.definition',
    formula: 'registry.parameterKey.allocation_solve_backend_id.formula',
  },
  'allocation.solve.node_limit': {
    label: 'registry.parameterKey.allocation_solve_node_limit.label',
    definition: 'registry.parameterKey.allocation_solve_node_limit.definition',
    formula: 'registry.parameterKey.allocation_solve_node_limit.formula',
  },
  'allocation.solve.relative_gap': {
    label: 'registry.parameterKey.allocation_solve_relative_gap.label',
    definition: 'registry.parameterKey.allocation_solve_relative_gap.definition',
    formula: 'registry.parameterKey.allocation_solve_relative_gap.formula',
  },
  'allocation.solve.seed': {
    label: 'registry.parameterKey.allocation_solve_seed.label',
    definition: 'registry.parameterKey.allocation_solve_seed.definition',
    formula: 'registry.parameterKey.allocation_solve_seed.formula',
  },
  'allocation.solve.thread_count': {
    label: 'registry.parameterKey.allocation_solve_thread_count.label',
    definition: 'registry.parameterKey.allocation_solve_thread_count.definition',
    formula: 'registry.parameterKey.allocation_solve_thread_count.formula',
  },
  'allocation.solve.time_limit_seconds': {
    label: 'registry.parameterKey.allocation_solve_time_limit_seconds.label',
    definition: 'registry.parameterKey.allocation_solve_time_limit_seconds.definition',
    formula: 'registry.parameterKey.allocation_solve_time_limit_seconds.formula',
  },
  'assortment_cap_multiple': {
    label: 'registry.parameterKey.assortment_cap_multiple.label',
    definition: 'registry.parameterKey.assortment_cap_multiple.definition',
    formula: 'registry.parameterKey.assortment_cap_multiple.formula',
  },
  'assortment_min_class_articles': {
    label: 'registry.parameterKey.assortment_min_class_articles.label',
    definition: 'registry.parameterKey.assortment_min_class_articles.definition',
    formula: 'registry.parameterKey.assortment_min_class_articles.formula',
  },
  'assortment_min_peer_doors': {
    label: 'registry.parameterKey.assortment_min_peer_doors.label',
    definition: 'registry.parameterKey.assortment_min_peer_doors.definition',
    formula: 'registry.parameterKey.assortment_min_peer_doors.formula',
  },
  'attribute_dominant_value_ceiling': {
    label: 'registry.parameterKey.attribute_dominant_value_ceiling.label',
    definition: 'registry.parameterKey.attribute_dominant_value_ceiling.definition',
    formula: 'registry.parameterKey.attribute_dominant_value_ceiling.formula',
  },
  'attribute_minimum_improvement': {
    label: 'registry.parameterKey.attribute_minimum_improvement.label',
    definition: 'registry.parameterKey.attribute_minimum_improvement.definition',
    formula: 'registry.parameterKey.attribute_minimum_improvement.formula',
  },
  'attribute_minimum_option_weeks': {
    label: 'registry.parameterKey.attribute_minimum_option_weeks.label',
    definition: 'registry.parameterKey.attribute_minimum_option_weeks.definition',
    formula: 'registry.parameterKey.attribute_minimum_option_weeks.formula',
  },
  'attribute_minimum_options': {
    label: 'registry.parameterKey.attribute_minimum_options.label',
    definition: 'registry.parameterKey.attribute_minimum_options.definition',
    formula: 'registry.parameterKey.attribute_minimum_options.formula',
  },
  'attribute_singleton_share_ceiling': {
    label: 'registry.parameterKey.attribute_singleton_share_ceiling.label',
    definition: 'registry.parameterKey.attribute_singleton_share_ceiling.definition',
    formula: 'registry.parameterKey.attribute_singleton_share_ceiling.formula',
  },
  'campaign.response.backtest_origin_from': {
    label: 'registry.parameterKey.campaign_response_backtest_origin_from.label',
    definition: 'registry.parameterKey.campaign_response_backtest_origin_from.definition',
    formula: 'registry.parameterKey.campaign_response_backtest_origin_from.formula',
  },
  'campaign.response.bootstrap_block_weeks': {
    label: 'registry.parameterKey.campaign_response_bootstrap_block_weeks.label',
    definition: 'registry.parameterKey.campaign_response_bootstrap_block_weeks.definition',
    formula: 'registry.parameterKey.campaign_response_bootstrap_block_weeks.formula',
  },
  'campaign.response.bootstrap_replicates': {
    label: 'registry.parameterKey.campaign_response_bootstrap_replicates.label',
    definition: 'registry.parameterKey.campaign_response_bootstrap_replicates.definition',
    formula: 'registry.parameterKey.campaign_response_bootstrap_replicates.formula',
  },
  'campaign.response.bootstrap_seed': {
    label: 'registry.parameterKey.campaign_response_bootstrap_seed.label',
    definition: 'registry.parameterKey.campaign_response_bootstrap_seed.definition',
    formula: 'registry.parameterKey.campaign_response_bootstrap_seed.formula',
  },
  'campaign.response.coverage_band_high': {
    label: 'registry.parameterKey.campaign_response_coverage_band_high.label',
    definition: 'registry.parameterKey.campaign_response_coverage_band_high.definition',
    formula: 'registry.parameterKey.campaign_response_coverage_band_high.formula',
  },
  'campaign.response.coverage_band_low': {
    label: 'registry.parameterKey.campaign_response_coverage_band_low.label',
    definition: 'registry.parameterKey.campaign_response_coverage_band_low.definition',
    formula: 'registry.parameterKey.campaign_response_coverage_band_low.formula',
  },
  'campaign.response.gate_min_predictions': {
    label: 'registry.parameterKey.campaign_response_gate_min_predictions.label',
    definition: 'registry.parameterKey.campaign_response_gate_min_predictions.definition',
    formula: 'registry.parameterKey.campaign_response_gate_min_predictions.formula',
  },
  'campaign.response.max_interval_log': {
    label: 'registry.parameterKey.campaign_response_max_interval_log.label',
    definition: 'registry.parameterKey.campaign_response_max_interval_log.definition',
    formula: 'registry.parameterKey.campaign_response_max_interval_log.formula',
  },
  'campaign.response.min_window_days': {
    label: 'registry.parameterKey.campaign_response_min_window_days.label',
    definition: 'registry.parameterKey.campaign_response_min_window_days.definition',
    formula: 'registry.parameterKey.campaign_response_min_window_days.formula',
  },
  'campaign.response.min_windows': {
    label: 'registry.parameterKey.campaign_response_min_windows.label',
    definition: 'registry.parameterKey.campaign_response_min_windows.definition',
    formula: 'registry.parameterKey.campaign_response_min_windows.formula',
  },
  'campaign.response.neighbour_days': {
    label: 'registry.parameterKey.campaign_response_neighbour_days.label',
    definition: 'registry.parameterKey.campaign_response_neighbour_days.definition',
    formula: 'registry.parameterKey.campaign_response_neighbour_days.formula',
  },
  'campaign.response.pull_forward_days': {
    label: 'registry.parameterKey.campaign_response_pull_forward_days.label',
    definition: 'registry.parameterKey.campaign_response_pull_forward_days.definition',
    formula: 'registry.parameterKey.campaign_response_pull_forward_days.formula',
  },
  'campaign.simulation.draws': {
    label: 'registry.parameterKey.campaign_simulation_draws.label',
    definition: 'registry.parameterKey.campaign_simulation_draws.definition',
    formula: 'registry.parameterKey.campaign_simulation_draws.formula',
  },
  'campaign.simulation.max_window_days': {
    label: 'registry.parameterKey.campaign_simulation_max_window_days.label',
    definition: 'registry.parameterKey.campaign_simulation_max_window_days.definition',
    formula: 'registry.parameterKey.campaign_simulation_max_window_days.formula',
  },
  'campaign.simulation.price_lookback_days': {
    label: 'registry.parameterKey.campaign_simulation_price_lookback_days.label',
    definition: 'registry.parameterKey.campaign_simulation_price_lookback_days.definition',
    formula: 'registry.parameterKey.campaign_simulation_price_lookback_days.formula',
  },
  'campaign.simulation.seed': {
    label: 'registry.parameterKey.campaign_simulation_seed.label',
    definition: 'registry.parameterKey.campaign_simulation_seed.definition',
    formula: 'registry.parameterKey.campaign_simulation_seed.formula',
  },
  'door_transport_frequency_days': {
    label: 'registry.parameterKey.door_transport_frequency_days.label',
    definition: 'registry.parameterKey.door_transport_frequency_days.definition',
    formula: 'registry.parameterKey.door_transport_frequency_days.formula',
  },
  'elasticity_pool_minimum_members': {
    label: 'registry.parameterKey.elasticity_pool_minimum_members.label',
    definition: 'registry.parameterKey.elasticity_pool_minimum_members.definition',
    formula: 'registry.parameterKey.elasticity_pool_minimum_members.formula',
  },
  'elasticity_pool_minimum_observations': {
    label: 'registry.parameterKey.elasticity_pool_minimum_observations.label',
    definition: 'registry.parameterKey.elasticity_pool_minimum_observations.definition',
    formula: 'registry.parameterKey.elasticity_pool_minimum_observations.formula',
  },
  'evidence.retention_weeks_after_season_end': {
    label: 'registry.parameterKey.evidence_retention_weeks_after_season_end.label',
    definition: 'registry.parameterKey.evidence_retention_weeks_after_season_end.definition',
    formula: 'registry.parameterKey.evidence_retention_weeks_after_season_end.formula',
  },
  'elasticity_coefficient_denominator': {
    label: 'registry.parameterKey.elasticity_coefficient_denominator.label',
    definition: 'registry.parameterKey.elasticity_coefficient_denominator.definition',
    formula: 'registry.parameterKey.elasticity_coefficient_denominator.formula',
  },
  'elasticity_confidence_level': {
    label: 'registry.parameterKey.elasticity_confidence_level.label',
    definition: 'registry.parameterKey.elasticity_confidence_level.definition',
    formula: 'registry.parameterKey.elasticity_confidence_level.formula',
  },
  'elasticity_coverage_target': {
    label: 'registry.parameterKey.elasticity_coverage_target.label',
    definition: 'registry.parameterKey.elasticity_coverage_target.definition',
    formula: 'registry.parameterKey.elasticity_coverage_target.formula',
  },
  'elasticity_detrend': {
    label: 'registry.parameterKey.elasticity_detrend.label',
    definition: 'registry.parameterKey.elasticity_detrend.definition',
    formula: 'registry.parameterKey.elasticity_detrend.formula',
  },
  'elasticity_include_promotional': {
    label: 'registry.parameterKey.elasticity_include_promotional.label',
    definition: 'registry.parameterKey.elasticity_include_promotional.definition',
    formula: 'registry.parameterKey.elasticity_include_promotional.formula',
  },
  'elasticity_maximum_interval_width': {
    label: 'registry.parameterKey.elasticity_maximum_interval_width.label',
    definition: 'registry.parameterKey.elasticity_maximum_interval_width.definition',
    formula: 'registry.parameterKey.elasticity_maximum_interval_width.formula',
  },
  'elasticity_minimum_observations': {
    label: 'registry.parameterKey.elasticity_minimum_observations.label',
    definition: 'registry.parameterKey.elasticity_minimum_observations.definition',
    formula: 'registry.parameterKey.elasticity_minimum_observations.formula',
  },
  'elasticity_minimum_price_variation': {
    label: 'registry.parameterKey.elasticity_minimum_price_variation.label',
    definition: 'registry.parameterKey.elasticity_minimum_price_variation.definition',
    formula: 'registry.parameterKey.elasticity_minimum_price_variation.formula',
  },
  'elasticity_model_version_id': {
    label: 'registry.parameterKey.elasticity_model_version_id.label',
    definition: 'registry.parameterKey.elasticity_model_version_id.definition',
    formula: 'registry.parameterKey.elasticity_model_version_id.formula',
  },
  'elasticity_parent_family_scheme_id': {
    label: 'registry.parameterKey.elasticity_parent_family_scheme_id.label',
    definition: 'registry.parameterKey.elasticity_parent_family_scheme_id.definition',
    formula: 'registry.parameterKey.elasticity_parent_family_scheme_id.formula',
  },
  'elasticity_parent_family_version_id': {
    label: 'registry.parameterKey.elasticity_parent_family_version_id.label',
    definition: 'registry.parameterKey.elasticity_parent_family_version_id.definition',
    formula: 'registry.parameterKey.elasticity_parent_family_version_id.formula',
  },
  'elasticity_prior_strength': {
    label: 'registry.parameterKey.elasticity_prior_strength.label',
    definition: 'registry.parameterKey.elasticity_prior_strength.definition',
    formula: 'registry.parameterKey.elasticity_prior_strength.formula',
  },
  'elasticity_sanity_band_lower': {
    label: 'registry.parameterKey.elasticity_sanity_band_lower.label',
    definition: 'registry.parameterKey.elasticity_sanity_band_lower.definition',
    formula: 'registry.parameterKey.elasticity_sanity_band_lower.formula',
  },
  'elasticity_sanity_band_upper': {
    label: 'registry.parameterKey.elasticity_sanity_band_upper.label',
    definition: 'registry.parameterKey.elasticity_sanity_band_upper.definition',
    formula: 'registry.parameterKey.elasticity_sanity_band_upper.formula',
  },
  'exception.dismissal_ceiling': {
    label: 'registry.parameterKey.exception_dismissal_ceiling.label',
    definition: 'registry.parameterKey.exception_dismissal_ceiling.definition',
    formula: 'registry.parameterKey.exception_dismissal_ceiling.formula',
  },
  'exception.reopen_reason_vocabulary': {
    label: 'registry.parameterKey.exception_reopen_reason_vocabulary.label',
    definition: 'registry.parameterKey.exception_reopen_reason_vocabulary.definition',
    formula: 'registry.parameterKey.exception_reopen_reason_vocabulary.formula',
  },
  // Owner ruling 2026-10-06 (bata-data issue 65; bata-platform ADR 8011).
  'exception.rule.cycle.failed_or_empty.threshold': {
    label: 'registry.parameterKey.exception_rule_cycle_failed_or_empty_threshold.label',
    definition: 'registry.parameterKey.exception_rule_cycle_failed_or_empty_threshold.definition',
    formula: 'registry.parameterKey.exception_rule_cycle_failed_or_empty_threshold.formula',
  },
  'exception.rule.inseason.cover_below.min_weekly_demand': {
    label: 'registry.parameterKey.exception_rule_inseason_cover_below_min_weekly_demand.label',
    definition: 'registry.parameterKey.exception_rule_inseason_cover_below_min_weekly_demand.definition',
    formula: 'registry.parameterKey.exception_rule_inseason_cover_below_min_weekly_demand.formula',
  },
  'exception.rule.inseason.cover_below.threshold': {
    label: 'registry.parameterKey.exception_rule_inseason_cover_below_threshold.label',
    definition: 'registry.parameterKey.exception_rule_inseason_cover_below_threshold.definition',
    formula: 'registry.parameterKey.exception_rule_inseason_cover_below_threshold.formula',
  },
  'exception.rule.inseason.forecast_bias.threshold': {
    label: 'registry.parameterKey.exception_rule_inseason_forecast_bias_threshold.label',
    definition: 'registry.parameterKey.exception_rule_inseason_forecast_bias_threshold.definition',
    formula: 'registry.parameterKey.exception_rule_inseason_forecast_bias_threshold.formula',
  },
  'exception.rule.inseason.forecast_bias.window_weeks': {
    label: 'registry.parameterKey.exception_rule_inseason_forecast_bias_window_weeks.label',
    definition: 'registry.parameterKey.exception_rule_inseason_forecast_bias_window_weeks.definition',
    formula: 'registry.parameterKey.exception_rule_inseason_forecast_bias_window_weeks.formula',
  },
  'exception.rule.inseason.size_run_broken.threshold': {
    label: 'registry.parameterKey.exception_rule_inseason_size_run_broken_threshold.label',
    definition: 'registry.parameterKey.exception_rule_inseason_size_run_broken_threshold.definition',
    formula: 'registry.parameterKey.exception_rule_inseason_size_run_broken_threshold.formula',
  },
  'exception.snooze_max_days': {
    label: 'registry.parameterKey.exception_snooze_max_days.label',
    definition: 'registry.parameterKey.exception_snooze_max_days.definition',
    formula: 'registry.parameterKey.exception_snooze_max_days.formula',
  },
  'exit.lifecycle_phase.inferred_carry_months': {
    label: 'registry.parameterKey.exit_lifecycle_phase_inferred_carry_months.label',
    definition: 'registry.parameterKey.exit_lifecycle_phase_inferred_carry_months.definition',
    formula: 'registry.parameterKey.exit_lifecycle_phase_inferred_carry_months.formula',
  },
  'exit.lifecycle_transition.age_weeks_min': {
    label: 'registry.parameterKey.exit_lifecycle_transition_age_weeks_min.label',
    definition: 'registry.parameterKey.exit_lifecycle_transition_age_weeks_min.definition',
    formula: 'registry.parameterKey.exit_lifecycle_transition_age_weeks_min.formula',
  },
  'exit.lifecycle_transition.broken_door_share_min': {
    label: 'registry.parameterKey.exit_lifecycle_transition_broken_door_share_min.label',
    definition: 'registry.parameterKey.exit_lifecycle_transition_broken_door_share_min.definition',
    formula: 'registry.parameterKey.exit_lifecycle_transition_broken_door_share_min.formula',
  },
  'exit.lifecycle_transition.cover_weeks_min': {
    label: 'registry.parameterKey.exit_lifecycle_transition_cover_weeks_min.label',
    definition: 'registry.parameterKey.exit_lifecycle_transition_cover_weeks_min.definition',
    formula: 'registry.parameterKey.exit_lifecycle_transition_cover_weeks_min.formula',
  },
  'exit.lifecycle_transition.decision_reason_vocabulary': {
    label: 'registry.parameterKey.exit_lifecycle_transition_decision_reason_vocabulary.label',
    definition: 'registry.parameterKey.exit_lifecycle_transition_decision_reason_vocabulary.definition',
    formula: 'registry.parameterKey.exit_lifecycle_transition_decision_reason_vocabulary.formula',
  },
  'exit.lifecycle_transition.markdown_deepening_min': {
    label: 'registry.parameterKey.exit_lifecycle_transition_markdown_deepening_min.label',
    definition: 'registry.parameterKey.exit_lifecycle_transition_markdown_deepening_min.definition',
    formula: 'registry.parameterKey.exit_lifecycle_transition_markdown_deepening_min.formula',
  },
  'exit.lifecycle_transition.markdown_trend_weeks': {
    label: 'registry.parameterKey.exit_lifecycle_transition_markdown_trend_weeks.label',
    definition: 'registry.parameterKey.exit_lifecycle_transition_markdown_trend_weeks.definition',
    formula: 'registry.parameterKey.exit_lifecycle_transition_markdown_trend_weeks.formula',
  },
  'exit.lifecycle_transition.minimum_age_months': {
    label: 'registry.parameterKey.exit_lifecycle_transition_minimum_age_months.label',
    definition: 'registry.parameterKey.exit_lifecycle_transition_minimum_age_months.definition',
    formula: 'registry.parameterKey.exit_lifecycle_transition_minimum_age_months.formula',
  },
  'exit.lifecycle_transition.minimum_signals': {
    label: 'registry.parameterKey.exit_lifecycle_transition_minimum_signals.label',
    definition: 'registry.parameterKey.exit_lifecycle_transition_minimum_signals.definition',
    formula: 'registry.parameterKey.exit_lifecycle_transition_minimum_signals.formula',
  },
  'exit.lifecycle_transition.protected_end_of_life_depth_min': {
    label: 'registry.parameterKey.exit_lifecycle_transition_protected_end_of_life_depth_min.label',
    definition: 'registry.parameterKey.exit_lifecycle_transition_protected_end_of_life_depth_min.definition',
    formula: 'registry.parameterKey.exit_lifecycle_transition_protected_end_of_life_depth_min.formula',
  },
  'exit.lifecycle_transition.reason_code_vocabulary': {
    label: 'registry.parameterKey.exit_lifecycle_transition_reason_code_vocabulary.label',
    definition: 'registry.parameterKey.exit_lifecycle_transition_reason_code_vocabulary.definition',
    formula: 'registry.parameterKey.exit_lifecycle_transition_reason_code_vocabulary.formula',
  },
  'exit.lifecycle_transition.sell_through_shortfall_min': {
    label: 'registry.parameterKey.exit_lifecycle_transition_sell_through_shortfall_min.label',
    definition: 'registry.parameterKey.exit_lifecycle_transition_sell_through_shortfall_min.definition',
    formula: 'registry.parameterKey.exit_lifecycle_transition_sell_through_shortfall_min.formula',
  },
  'exit.outlet_exit.decision_reason_vocabulary': {
    label: 'registry.parameterKey.exit_outlet_exit_decision_reason_vocabulary.label',
    definition: 'registry.parameterKey.exit_outlet_exit_decision_reason_vocabulary.definition',
    formula: 'registry.parameterKey.exit_outlet_exit_decision_reason_vocabulary.formula',
  },
  'exit.outlet_exit.outlet_discount': {
    label: 'registry.parameterKey.exit_outlet_exit_outlet_discount.label',
    definition: 'registry.parameterKey.exit_outlet_exit_outlet_discount.definition',
    formula: 'registry.parameterKey.exit_outlet_exit_outlet_discount.formula',
  },
  'exit.outlet_exit.reason_code_vocabulary': {
    label: 'registry.parameterKey.exit_outlet_exit_reason_code_vocabulary.label',
    definition: 'registry.parameterKey.exit_outlet_exit_reason_code_vocabulary.definition',
    formula: 'registry.parameterKey.exit_outlet_exit_reason_code_vocabulary.formula',
  },
  'exit_test.arm': {
    label: 'registry.parameterKey.exit_test_arm.label',
    definition: 'registry.parameterKey.exit_test_arm.definition',
    formula: 'registry.parameterKey.exit_test_arm.formula',
  },
  'exit_test.confidence_level': {
    label: 'registry.parameterKey.exit_test_confidence_level.label',
    definition: 'registry.parameterKey.exit_test_confidence_level.definition',
    formula: 'registry.parameterKey.exit_test_confidence_level.formula',
  },
  'exit_test.freeze_caveat': {
    label: 'registry.parameterKey.exit_test_freeze_caveat.label',
    definition: 'registry.parameterKey.exit_test_freeze_caveat.definition',
    formula: 'registry.parameterKey.exit_test_freeze_caveat.formula',
  },
  'exit_test.matched_control': {
    label: 'registry.parameterKey.exit_test_matched_control.label',
    definition: 'registry.parameterKey.exit_test_matched_control.definition',
    formula: 'registry.parameterKey.exit_test_matched_control.formula',
  },
  'exit_test.maximum_match_distance': {
    label: 'registry.parameterKey.exit_test_maximum_match_distance.label',
    definition: 'registry.parameterKey.exit_test_maximum_match_distance.definition',
    formula: 'registry.parameterKey.exit_test_maximum_match_distance.formula',
  },
  'exit_test.maximum_standardised_difference': {
    label: 'registry.parameterKey.exit_test_maximum_standardised_difference.label',
    definition: 'registry.parameterKey.exit_test_maximum_standardised_difference.definition',
    formula: 'registry.parameterKey.exit_test_maximum_standardised_difference.formula',
  },
  'exit_test.maximum_usable_relative_mde': {
    label: 'registry.parameterKey.exit_test_maximum_usable_relative_mde.label',
    definition: 'registry.parameterKey.exit_test_maximum_usable_relative_mde.definition',
    formula: 'registry.parameterKey.exit_test_maximum_usable_relative_mde.formula',
  },
  'exit_test.minimum_arm_stores': {
    label: 'registry.parameterKey.exit_test_minimum_arm_stores.label',
    definition: 'registry.parameterKey.exit_test_minimum_arm_stores.definition',
    formula: 'registry.parameterKey.exit_test_minimum_arm_stores.formula',
  },
  'exit_test.minimum_baseline_weeks': {
    label: 'registry.parameterKey.exit_test_minimum_baseline_weeks.label',
    definition: 'registry.parameterKey.exit_test_minimum_baseline_weeks.definition',
    formula: 'registry.parameterKey.exit_test_minimum_baseline_weeks.formula',
  },
  'exit_test.pre_period_first_month': {
    label: 'registry.parameterKey.exit_test_pre_period_first_month.label',
    definition: 'registry.parameterKey.exit_test_pre_period_first_month.definition',
    formula: 'registry.parameterKey.exit_test_pre_period_first_month.formula',
  },
  'exit_test.pre_period_last_month': {
    label: 'registry.parameterKey.exit_test_pre_period_last_month.label',
    definition: 'registry.parameterKey.exit_test_pre_period_last_month.definition',
    formula: 'registry.parameterKey.exit_test_pre_period_last_month.formula',
  },
  'exit_test.statistical_power': {
    label: 'registry.parameterKey.exit_test_statistical_power.label',
    definition: 'registry.parameterKey.exit_test_statistical_power.definition',
    formula: 'registry.parameterKey.exit_test_statistical_power.formula',
  },
  // bata-reqs ADR 9003 O5 (bata-platform ADR 9054): the Health app's management targets.
  'health.target.full_price_availability': {
    label: 'registry.parameterKey.health_target_full_price_availability.label',
    definition: 'registry.parameterKey.health_target_full_price_availability.definition',
    formula: 'registry.parameterKey.health_target_full_price_availability.formula',
  },
  'health.target.cells_served_in_stock': {
    label: 'registry.parameterKey.health_target_cells_served_in_stock.label',
    definition: 'registry.parameterKey.health_target_cells_served_in_stock.definition',
    formula: 'registry.parameterKey.health_target_cells_served_in_stock.formula',
  },
  'health.target.broken_size_runs': {
    label: 'registry.parameterKey.health_target_broken_size_runs.label',
    definition: 'registry.parameterKey.health_target_broken_size_runs.definition',
    formula: 'registry.parameterKey.health_target_broken_size_runs.formula',
  },
  'health.target.cells_low_on_cover': {
    label: 'registry.parameterKey.health_target_cells_low_on_cover.label',
    definition: 'registry.parameterKey.health_target_cells_low_on_cover.definition',
    formula: 'registry.parameterKey.health_target_cells_low_on_cover.formula',
  },
  'health.target.aged_cover_units': {
    label: 'registry.parameterKey.health_target_aged_cover_units.label',
    definition: 'registry.parameterKey.health_target_aged_cover_units.definition',
    formula: 'registry.parameterKey.health_target_aged_cover_units.formula',
  },
  'health.target.sell_through_season_to_date': {
    label: 'registry.parameterKey.health_target_sell_through_season_to_date.label',
    definition: 'registry.parameterKey.health_target_sell_through_season_to_date.definition',
    formula: 'registry.parameterKey.health_target_sell_through_season_to_date.formula',
  },
  'health.target.full_price_sell_through': {
    label: 'registry.parameterKey.health_target_full_price_sell_through.label',
    definition: 'registry.parameterKey.health_target_full_price_sell_through.definition',
    formula: 'registry.parameterKey.health_target_full_price_sell_through.formula',
  },
  'health.target.full_price_share_of_sales': {
    label: 'registry.parameterKey.health_target_full_price_share_of_sales.label',
    definition: 'registry.parameterKey.health_target_full_price_share_of_sales.definition',
    formula: 'registry.parameterKey.health_target_full_price_share_of_sales.formula',
  },
  'health.target.markdown_exposure_at_cost': {
    label: 'registry.parameterKey.health_target_markdown_exposure_at_cost.label',
    definition: 'registry.parameterKey.health_target_markdown_exposure_at_cost.definition',
    formula: 'registry.parameterKey.health_target_markdown_exposure_at_cost.formula',
  },
  'health.target.amber_tolerance': {
    label: 'registry.parameterKey.health_target_amber_tolerance.label',
    definition: 'registry.parameterKey.health_target_amber_tolerance.definition',
    formula: 'registry.parameterKey.health_target_amber_tolerance.formula',
  },
  // bata-platform ADR 9053 amended: the edges of Health ▸ Coverage's weeks-of-cover distribution.
  'health.coverage.cover_band_weeks': {
    label: 'registry.parameterKey.health_coverage_cover_band_weeks.label',
    definition: 'registry.parameterKey.health_coverage_cover_band_weeks.definition',
    formula: 'registry.parameterKey.health_coverage_cover_band_weeks.formula',
  },
  // Platform ADR 9056 (owner rulings 2026-11-02): the one Scope the daily one move run is posed over.
  'compacting.run_scope': {
    label: 'registry.parameterKey.compacting_run_scope.label',
    definition: 'registry.parameterKey.compacting_run_scope.definition',
    formula: 'registry.parameterKey.compacting_run_scope.formula',
  },
  'inseason.replenishment.run_scopes': {
    label: 'registry.parameterKey.inseason_replenishment_run_scopes.label',
    definition: 'registry.parameterKey.inseason_replenishment_run_scopes.definition',
    formula: 'registry.parameterKey.inseason_replenishment_run_scopes.formula',
  },
  'markdown.fallback_elasticity': {
    label: 'registry.parameterKey.markdown_fallback_elasticity.label',
    definition: 'registry.parameterKey.markdown_fallback_elasticity.definition',
    formula: 'registry.parameterKey.markdown_fallback_elasticity.formula',
  },
  'markdown.outlet_max_months': {
    label: 'registry.parameterKey.markdown_outlet_max_months.label',
    definition: 'registry.parameterKey.markdown_outlet_max_months.definition',
    formula: 'registry.parameterKey.markdown_outlet_max_months.formula',
  },
  'markdown.outlet_maximum_depth': {
    label: 'registry.parameterKey.markdown_outlet_maximum_depth.label',
    definition: 'registry.parameterKey.markdown_outlet_maximum_depth.definition',
    formula: 'registry.parameterKey.markdown_outlet_maximum_depth.formula',
  },
  'markdown.outlet_solve_horizon_months': {
    label: 'registry.parameterKey.markdown_outlet_solve_horizon_months.label',
    definition: 'registry.parameterKey.markdown_outlet_solve_horizon_months.definition',
    formula: 'registry.parameterKey.markdown_outlet_solve_horizon_months.formula',
  },
  'markdown.outlet_terminal_residual_share': {
    label: 'registry.parameterKey.markdown_outlet_terminal_residual_share.label',
    definition: 'registry.parameterKey.markdown_outlet_terminal_residual_share.definition',
    formula: 'registry.parameterKey.markdown_outlet_terminal_residual_share.formula',
  },
  'markdown_test.arm': {
    label: 'registry.parameterKey.markdown_test_arm.label',
    definition: 'registry.parameterKey.markdown_test_arm.definition',
    formula: 'registry.parameterKey.markdown_test_arm.formula',
  },
  'markdown_test.confidence_level': {
    label: 'registry.parameterKey.markdown_test_confidence_level.label',
    definition: 'registry.parameterKey.markdown_test_confidence_level.definition',
    formula: 'registry.parameterKey.markdown_test_confidence_level.formula',
  },
  'markdown_test.freeze_caveat': {
    label: 'registry.parameterKey.markdown_test_freeze_caveat.label',
    definition: 'registry.parameterKey.markdown_test_freeze_caveat.definition',
    formula: 'registry.parameterKey.markdown_test_freeze_caveat.formula',
  },
  'markdown_test.matched_control': {
    label: 'registry.parameterKey.markdown_test_matched_control.label',
    definition: 'registry.parameterKey.markdown_test_matched_control.definition',
    formula: 'registry.parameterKey.markdown_test_matched_control.formula',
  },
  'markdown_test.maximum_match_distance': {
    label: 'registry.parameterKey.markdown_test_maximum_match_distance.label',
    definition: 'registry.parameterKey.markdown_test_maximum_match_distance.definition',
    formula: 'registry.parameterKey.markdown_test_maximum_match_distance.formula',
  },
  'markdown_test.maximum_standardised_difference': {
    label: 'registry.parameterKey.markdown_test_maximum_standardised_difference.label',
    definition: 'registry.parameterKey.markdown_test_maximum_standardised_difference.definition',
    formula: 'registry.parameterKey.markdown_test_maximum_standardised_difference.formula',
  },
  'markdown_test.maximum_usable_relative_mde': {
    label: 'registry.parameterKey.markdown_test_maximum_usable_relative_mde.label',
    definition: 'registry.parameterKey.markdown_test_maximum_usable_relative_mde.definition',
    formula: 'registry.parameterKey.markdown_test_maximum_usable_relative_mde.formula',
  },
  'markdown_test.minimum_arm_stores': {
    label: 'registry.parameterKey.markdown_test_minimum_arm_stores.label',
    definition: 'registry.parameterKey.markdown_test_minimum_arm_stores.definition',
    formula: 'registry.parameterKey.markdown_test_minimum_arm_stores.formula',
  },
  'markdown_test.minimum_baseline_weeks': {
    label: 'registry.parameterKey.markdown_test_minimum_baseline_weeks.label',
    definition: 'registry.parameterKey.markdown_test_minimum_baseline_weeks.definition',
    formula: 'registry.parameterKey.markdown_test_minimum_baseline_weeks.formula',
  },
  'markdown_test.pre_period_first_month': {
    label: 'registry.parameterKey.markdown_test_pre_period_first_month.label',
    definition: 'registry.parameterKey.markdown_test_pre_period_first_month.definition',
    formula: 'registry.parameterKey.markdown_test_pre_period_first_month.formula',
  },
  'markdown_test.pre_period_last_month': {
    label: 'registry.parameterKey.markdown_test_pre_period_last_month.label',
    definition: 'registry.parameterKey.markdown_test_pre_period_last_month.definition',
    formula: 'registry.parameterKey.markdown_test_pre_period_last_month.formula',
  },
  'markdown_test.statistical_power': {
    label: 'registry.parameterKey.markdown_test_statistical_power.label',
    definition: 'registry.parameterKey.markdown_test_statistical_power.definition',
    formula: 'registry.parameterKey.markdown_test_statistical_power.formula',
  },
  'markdown_test.step_depth': {
    label: 'registry.parameterKey.markdown_test_step_depth.label',
    definition: 'registry.parameterKey.markdown_test_step_depth.definition',
    formula: 'registry.parameterKey.markdown_test_step_depth.formula',
  },
  'mlplatform.sell_probability.band.': {
    label: 'registry.parameterKey.mlplatform_sell_probability_band_.label',
    definition: 'registry.parameterKey.mlplatform_sell_probability_band_.definition',
    formula: 'registry.parameterKey.mlplatform_sell_probability_band_.formula',
  },
  'mlplatform.sell_probability.censored_high_key': {
    label: 'registry.parameterKey.mlplatform_sell_probability_censored_high_key.label',
    definition: 'registry.parameterKey.mlplatform_sell_probability_censored_high_key.definition',
    formula: 'registry.parameterKey.mlplatform_sell_probability_censored_high_key.formula',
  },
  'mlplatform.sell_probability.censored_low_key': {
    label: 'registry.parameterKey.mlplatform_sell_probability_censored_low_key.label',
    definition: 'registry.parameterKey.mlplatform_sell_probability_censored_low_key.definition',
    formula: 'registry.parameterKey.mlplatform_sell_probability_censored_low_key.formula',
  },
  'module.preseason.cadence': {
    label: 'registry.parameterKey.module_preseason_cadence.label',
    definition: 'registry.parameterKey.module_preseason_cadence.definition',
    formula: 'registry.parameterKey.module_preseason_cadence.formula',
  },
  'module.preseason.enabled': {
    label: 'registry.parameterKey.module_preseason_enabled.label',
    definition: 'registry.parameterKey.module_preseason_enabled.definition',
    formula: 'registry.parameterKey.module_preseason_enabled.formula',
  },
  'objective.carrying_cost_rate_per_week': {
    label: 'registry.parameterKey.objective_carrying_cost_rate_per_week.label',
    definition: 'registry.parameterKey.objective_carrying_cost_rate_per_week.definition',
    formula: 'registry.parameterKey.objective_carrying_cost_rate_per_week.formula',
  },
  // bata-reqs ADR 9002 stage 3 ruling 3: below this many warehouse units an article passes to compacting.
  'objective.compacting_dc_units_threshold': {
    label: 'registry.parameterKey.objective_compacting_dc_units_threshold.label',
    definition: 'registry.parameterKey.objective_compacting_dc_units_threshold.definition',
    formula: 'registry.parameterKey.objective_compacting_dc_units_threshold.formula',
  },
  'objective.exit_realisation_rate': {
    label: 'registry.parameterKey.objective_exit_realisation_rate.label',
    definition: 'registry.parameterKey.objective_exit_realisation_rate.definition',
    formula: 'registry.parameterKey.objective_exit_realisation_rate.formula',
  },
  'objective.landed_cost_provenances': {
    label: 'registry.parameterKey.objective_landed_cost_provenances.label',
    definition: 'registry.parameterKey.objective_landed_cost_provenances.definition',
    formula: 'registry.parameterKey.objective_landed_cost_provenances.formula',
  },
  'objective.margin_reconciliation_tolerance': {
    label: 'registry.parameterKey.objective_margin_reconciliation_tolerance.label',
    definition: 'registry.parameterKey.objective_margin_reconciliation_tolerance.definition',
    formula: 'registry.parameterKey.objective_margin_reconciliation_tolerance.formula',
  },
  // bata-reqs ADR 9002 stage 1 (bata-platform `docs/parameters/objective.md`).
  'objective.move_valuation': {
    label: 'registry.parameterKey.objective_move_valuation.label',
    definition: 'registry.parameterKey.objective_move_valuation.definition',
    formula: 'registry.parameterKey.objective_move_valuation.formula',
  },
  'objective.season_end_realisation_rate': {
    label: 'registry.parameterKey.objective_season_end_realisation_rate.label',
    definition: 'registry.parameterKey.objective_season_end_realisation_rate.definition',
    formula: 'registry.parameterKey.objective_season_end_realisation_rate.formula',
  },
  'plan.cell_sources': {
    label: 'registry.parameterKey.plan_cell_sources.label',
    definition: 'registry.parameterKey.plan_cell_sources.definition',
    formula: 'registry.parameterKey.plan_cell_sources.formula',
  },
  'plan.reason_codes': {
    label: 'registry.parameterKey.plan_reason_codes.label',
    definition: 'registry.parameterKey.plan_reason_codes.definition',
    formula: 'registry.parameterKey.plan_reason_codes.formula',
  },
  'plan.sealed_statuses': {
    label: 'registry.parameterKey.plan_sealed_statuses.label',
    definition: 'registry.parameterKey.plan_sealed_statuses.definition',
    formula: 'registry.parameterKey.plan_sealed_statuses.formula',
  },
  'plan.version_kinds': {
    label: 'registry.parameterKey.plan_version_kinds.label',
    definition: 'registry.parameterKey.plan_version_kinds.definition',
    formula: 'registry.parameterKey.plan_version_kinds.formula',
  },
  'plan.version_statuses': {
    label: 'registry.parameterKey.plan_version_statuses.label',
    definition: 'registry.parameterKey.plan_version_statuses.definition',
    formula: 'registry.parameterKey.plan_version_statuses.formula',
  },
  'postseason.assortment_cluster_scheme': {
    label: 'registry.parameterKey.postseason_assortment_cluster_scheme.label',
    definition: 'registry.parameterKey.postseason_assortment_cluster_scheme.definition',
    formula: 'registry.parameterKey.postseason_assortment_cluster_scheme.formula',
  },
  'postseason.recovery_curve_class_scheme': {
    label: 'registry.parameterKey.postseason_recovery_curve_class_scheme.label',
    definition: 'registry.parameterKey.postseason_recovery_curve_class_scheme.definition',
    formula: 'registry.parameterKey.postseason_recovery_curve_class_scheme.formula',
  },
  // bata-reqs ADR 8001 decision 6.7 and ADR 8000 (bata-platform `services/repositories/parameter_catalogue.py`,
  // `docs/runbooks/recovery-lineage-deploy.md` §3 and §8): the re-fit's concurrency and the score's live switch.
  'postseason.recovery_parallel_groups': {
    label: 'registry.parameterKey.postseason_recovery_parallel_groups.label',
    definition: 'registry.parameterKey.postseason_recovery_parallel_groups.definition',
    formula: 'registry.parameterKey.postseason_recovery_parallel_groups.formula',
  },
  'postseason.recovery_score_live': {
    label: 'registry.parameterKey.postseason_recovery_score_live.label',
    definition: 'registry.parameterKey.postseason_recovery_score_live.definition',
    formula: 'registry.parameterKey.postseason_recovery_score_live.formula',
  },
  'preseason.cluster_fit.bootstrap_resamples': {
    label: 'registry.parameterKey.preseason_cluster_fit_bootstrap_resamples.label',
    definition: 'registry.parameterKey.preseason_cluster_fit_bootstrap_resamples.definition',
    formula: 'registry.parameterKey.preseason_cluster_fit_bootstrap_resamples.formula',
  },
  'preseason.cluster_fit.bootstrap_subsample_size': {
    label: 'registry.parameterKey.preseason_cluster_fit_bootstrap_subsample_size.label',
    definition: 'registry.parameterKey.preseason_cluster_fit_bootstrap_subsample_size.definition',
    formula: 'registry.parameterKey.preseason_cluster_fit_bootstrap_subsample_size.formula',
  },
  'preseason.cluster_fit.candidate_k': {
    label: 'registry.parameterKey.preseason_cluster_fit_candidate_k.label',
    definition: 'registry.parameterKey.preseason_cluster_fit_candidate_k.definition',
    formula: 'registry.parameterKey.preseason_cluster_fit_candidate_k.formula',
  },
  'preseason.cluster_fit.max_iterations': {
    label: 'registry.parameterKey.preseason_cluster_fit_max_iterations.label',
    definition: 'registry.parameterKey.preseason_cluster_fit_max_iterations.definition',
    formula: 'registry.parameterKey.preseason_cluster_fit_max_iterations.formula',
  },
  'preseason.cluster_fit.minimum_cluster_size': {
    label: 'registry.parameterKey.preseason_cluster_fit_minimum_cluster_size.label',
    definition: 'registry.parameterKey.preseason_cluster_fit_minimum_cluster_size.definition',
    formula: 'registry.parameterKey.preseason_cluster_fit_minimum_cluster_size.formula',
  },
  'preseason.cluster_fit.required_uplift': {
    label: 'registry.parameterKey.preseason_cluster_fit_required_uplift.label',
    definition: 'registry.parameterKey.preseason_cluster_fit_required_uplift.definition',
    formula: 'registry.parameterKey.preseason_cluster_fit_required_uplift.formula',
  },
  'preseason.cluster_fit.seed': {
    label: 'registry.parameterKey.preseason_cluster_fit_seed.label',
    definition: 'registry.parameterKey.preseason_cluster_fit_seed.definition',
    formula: 'registry.parameterKey.preseason_cluster_fit_seed.formula',
  },
  'preseason.cluster_fit.stability_target': {
    label: 'registry.parameterKey.preseason_cluster_fit_stability_target.label',
    definition: 'registry.parameterKey.preseason_cluster_fit_stability_target.definition',
    formula: 'registry.parameterKey.preseason_cluster_fit_stability_target.formula',
  },
  'preseason.cluster_fit.tolerance': {
    label: 'registry.parameterKey.preseason_cluster_fit_tolerance.label',
    definition: 'registry.parameterKey.preseason_cluster_fit_tolerance.definition',
    formula: 'registry.parameterKey.preseason_cluster_fit_tolerance.formula',
  },
  // bata-platform ADR 4002 (#921): the department-family fit of the CO cluster master.
  'preseason.cluster_fit.window_weeks': {
    label: 'registry.parameterKey.preseason_cluster_fit_window_weeks.label',
    definition: 'registry.parameterKey.preseason_cluster_fit_window_weeks.definition',
    formula: 'registry.parameterKey.preseason_cluster_fit_window_weeks.formula',
  },
  'preseason.cluster_fit.minimum_discrimination_cv': {
    label: 'registry.parameterKey.preseason_cluster_fit_minimum_discrimination_cv.label',
    definition: 'registry.parameterKey.preseason_cluster_fit_minimum_discrimination_cv.definition',
    formula: 'registry.parameterKey.preseason_cluster_fit_minimum_discrimination_cv.formula',
  },
  'preseason.cluster_fit.scheme_owner': {
    label: 'registry.parameterKey.preseason_cluster_fit_scheme_owner.label',
    definition: 'registry.parameterKey.preseason_cluster_fit_scheme_owner.definition',
    formula: 'registry.parameterKey.preseason_cluster_fit_scheme_owner.formula',
  },
  'preseason.size_curve.max_iterations': {
    label: 'registry.parameterKey.preseason_size_curve_max_iterations.label',
    definition: 'registry.parameterKey.preseason_size_curve_max_iterations.definition',
    formula: 'registry.parameterKey.preseason_size_curve_max_iterations.formula',
  },
  'preseason.size_curve.minimum_observed_units': {
    label: 'registry.parameterKey.preseason_size_curve_minimum_observed_units.label',
    definition: 'registry.parameterKey.preseason_size_curve_minimum_observed_units.definition',
    formula: 'registry.parameterKey.preseason_size_curve_minimum_observed_units.formula',
  },
  'preseason.size_curve.prior_strength': {
    label: 'registry.parameterKey.preseason_size_curve_prior_strength.label',
    definition: 'registry.parameterKey.preseason_size_curve_prior_strength.definition',
    formula: 'registry.parameterKey.preseason_size_curve_prior_strength.formula',
  },
  'preseason.size_curve.tolerance': {
    label: 'registry.parameterKey.preseason_size_curve_tolerance.label',
    definition: 'registry.parameterKey.preseason_size_curve_tolerance.definition',
    formula: 'registry.parameterKey.preseason_size_curve_tolerance.formula',
  },
  'preseason.size_curve.weighted_holdout': {
    label: 'registry.parameterKey.preseason_size_curve_weighted_holdout.label',
    definition: 'registry.parameterKey.preseason_size_curve_weighted_holdout.definition',
    formula: 'registry.parameterKey.preseason_size_curve_weighted_holdout.formula',
  },
  'product_image.absence_reason_codes': {
    label: 'registry.parameterKey.product_image_absence_reason_codes.label',
    definition: 'registry.parameterKey.product_image_absence_reason_codes.definition',
    formula: 'registry.parameterKey.product_image_absence_reason_codes.formula',
  },
  'programme.measurement.cover_band_weeks': {
    label: 'registry.parameterKey.programme_measurement_cover_band_weeks.label',
    definition: 'registry.parameterKey.programme_measurement_cover_band_weeks.definition',
    formula: 'registry.parameterKey.programme_measurement_cover_band_weeks.formula',
  },
  'programme.measurement.match_calliper': {
    label: 'registry.parameterKey.programme_measurement_match_calliper.label',
    definition: 'registry.parameterKey.programme_measurement_match_calliper.definition',
    formula: 'registry.parameterKey.programme_measurement_match_calliper.formula',
  },
  'programme.measurement.metrics_version': {
    label: 'registry.parameterKey.programme_measurement_metrics_version.label',
    definition: 'registry.parameterKey.programme_measurement_metrics_version.definition',
    formula: 'registry.parameterKey.programme_measurement_metrics_version.formula',
  },
  'programme.measurement.minimum_matched_pairs': {
    label: 'registry.parameterKey.programme_measurement_minimum_matched_pairs.label',
    definition: 'registry.parameterKey.programme_measurement_minimum_matched_pairs.definition',
    formula: 'registry.parameterKey.programme_measurement_minimum_matched_pairs.formula',
  },
  'programme.measurement.minimum_pre_periods': {
    label: 'registry.parameterKey.programme_measurement_minimum_pre_periods.label',
    definition: 'registry.parameterKey.programme_measurement_minimum_pre_periods.definition',
    formula: 'registry.parameterKey.programme_measurement_minimum_pre_periods.formula',
  },
  'programme.measurement.non_selling_days': {
    label: 'registry.parameterKey.programme_measurement_non_selling_days.label',
    definition: 'registry.parameterKey.programme_measurement_non_selling_days.definition',
    formula: 'registry.parameterKey.programme_measurement_non_selling_days.formula',
  },
  'programme.measurement.override_evaluation_horizon_days': {
    label: 'registry.parameterKey.programme_measurement_override_evaluation_horizon_days.label',
    definition: 'registry.parameterKey.programme_measurement_override_evaluation_horizon_days.definition',
    formula: 'registry.parameterKey.programme_measurement_override_evaluation_horizon_days.formula',
  },
  'programme.measurement.recapture_rate': {
    label: 'registry.parameterKey.programme_measurement_recapture_rate.label',
    definition: 'registry.parameterKey.programme_measurement_recapture_rate.definition',
    formula: 'registry.parameterKey.programme_measurement_recapture_rate.formula',
  },
  'programme.measurement.sign_off_state': {
    label: 'registry.parameterKey.programme_measurement_sign_off_state.label',
    definition: 'registry.parameterKey.programme_measurement_sign_off_state.definition',
    formula: 'registry.parameterKey.programme_measurement_sign_off_state.formula',
  },
  'programme.metric_definition.': {
    label: 'registry.parameterKey.programme_metric_definition_.label',
    definition: 'registry.parameterKey.programme_metric_definition_.definition',
    formula: 'registry.parameterKey.programme_metric_definition_.formula',
  },
  'programme.override_outcome.window_days': {
    label: 'registry.parameterKey.programme_override_outcome_window_days.label',
    definition: 'registry.parameterKey.programme_override_outcome_window_days.definition',
    formula: 'registry.parameterKey.programme_override_outcome_window_days.formula',
  },
  'promotion.mechanic_vocabulary': {
    label: 'registry.parameterKey.promotion_mechanic_vocabulary.label',
    definition: 'registry.parameterKey.promotion_mechanic_vocabulary.definition',
    formula: 'registry.parameterKey.promotion_mechanic_vocabulary.formula',
  },
  'exit.cadence_mode': {
    label: 'registry.parameterKey.exit_cadence_mode.label',
    definition: 'registry.parameterKey.exit_cadence_mode.definition',
    formula: 'registry.parameterKey.exit_cadence_mode.formula',
  },
  'exit.cadence.elasticity_steps': {
    label: 'registry.parameterKey.exit_cadence_elasticity_steps.label',
    definition: 'registry.parameterKey.exit_cadence_elasticity_steps.definition',
    formula: 'registry.parameterKey.exit_cadence_elasticity_steps.formula',
  },
  'exit.cadence.rate_safety_margin': {
    label: 'registry.parameterKey.exit_cadence_rate_safety_margin.label',
    definition: 'registry.parameterKey.exit_cadence_rate_safety_margin.definition',
    formula: 'registry.parameterKey.exit_cadence_rate_safety_margin.formula',
  },
  'exit.cadence.trailing_weeks': {
    label: 'registry.parameterKey.exit_cadence_trailing_weeks.label',
    definition: 'registry.parameterKey.exit_cadence_trailing_weeks.definition',
    formula: 'registry.parameterKey.exit_cadence_trailing_weeks.formula',
  },
  'exit.cadence.trigger_factor': {
    label: 'registry.parameterKey.exit_cadence_trigger_factor.label',
    definition: 'registry.parameterKey.exit_cadence_trigger_factor.definition',
    formula: 'registry.parameterKey.exit_cadence_trigger_factor.formula',
  },
  'exit.cadence.trigger_weeks': {
    label: 'registry.parameterKey.exit_cadence_trigger_weeks.label',
    definition: 'registry.parameterKey.exit_cadence_trigger_weeks.definition',
    formula: 'registry.parameterKey.exit_cadence_trigger_weeks.formula',
  },
  'exit.consolidation.fallback_elasticity': {
    label: 'registry.parameterKey.exit_consolidation_fallback_elasticity.label',
    definition: 'registry.parameterKey.exit_consolidation_fallback_elasticity.definition',
    formula: 'registry.parameterKey.exit_consolidation_fallback_elasticity.formula',
  },
  'exit.consolidation.pool_destination_store_types': {
    label: 'registry.parameterKey.exit_consolidation_pool_destination_store_types.label',
    definition: 'registry.parameterKey.exit_consolidation_pool_destination_store_types.definition',
    formula: 'registry.parameterKey.exit_consolidation_pool_destination_store_types.formula',
  },
  'exit.indifference_variation_floor': {
    label: 'registry.parameterKey.exit_indifference_variation_floor.label',
    definition: 'registry.parameterKey.exit_indifference_variation_floor.definition',
    formula: 'registry.parameterKey.exit_indifference_variation_floor.formula',
  },
  'exit.interim_cluster_scheme': {
    label: 'registry.parameterKey.exit_interim_cluster_scheme.label',
    definition: 'registry.parameterKey.exit_interim_cluster_scheme.definition',
    formula: 'registry.parameterKey.exit_interim_cluster_scheme.formula',
  },
  'exit.lifecycle_governed_by_cover': {
    label: 'registry.parameterKey.exit_lifecycle_governed_by_cover.label',
    definition: 'registry.parameterKey.exit_lifecycle_governed_by_cover.definition',
    formula: 'registry.parameterKey.exit_lifecycle_governed_by_cover.formula',
  },
  'exit.lane_decision_reason_vocabulary': {
    label: 'registry.parameterKey.exit_lane_decision_reason_vocabulary.label',
    definition: 'registry.parameterKey.exit_lane_decision_reason_vocabulary.definition',
    formula: 'registry.parameterKey.exit_lane_decision_reason_vocabulary.formula',
  },
  'exit.lost_sales.max_age_days': {
    label: 'registry.parameterKey.exit_lost_sales_max_age_days.label',
    definition: 'registry.parameterKey.exit_lost_sales_max_age_days.definition',
    formula: 'registry.parameterKey.exit_lost_sales_max_age_days.formula',
  },
  'exit.provisional_handover_date': {
    label: 'registry.parameterKey.exit_provisional_handover_date.label',
    definition: 'registry.parameterKey.exit_provisional_handover_date.definition',
    formula: 'registry.parameterKey.exit_provisional_handover_date.formula',
  },
  'exit.reason_code_vocabulary': {
    label: 'registry.parameterKey.exit_reason_code_vocabulary.label',
    definition: 'registry.parameterKey.exit_reason_code_vocabulary.definition',
    formula: 'registry.parameterKey.exit_reason_code_vocabulary.formula',
  },
  'exit.shadow.incumbent_price_tolerance': {
    label: 'registry.parameterKey.exit_shadow_incumbent_price_tolerance.label',
    definition: 'registry.parameterKey.exit_shadow_incumbent_price_tolerance.definition',
    formula: 'registry.parameterKey.exit_shadow_incumbent_price_tolerance.formula',
  },
  'exit.shadow.required_seasons': {
    label: 'registry.parameterKey.exit_shadow_required_seasons.label',
    definition: 'registry.parameterKey.exit_shadow_required_seasons.definition',
    formula: 'registry.parameterKey.exit_shadow_required_seasons.formula',
  },
  'exit.shadow.terminal_tolerance_units': {
    label: 'registry.parameterKey.exit_shadow_terminal_tolerance_units.label',
    definition: 'registry.parameterKey.exit_shadow_terminal_tolerance_units.definition',
    formula: 'registry.parameterKey.exit_shadow_terminal_tolerance_units.formula',
  },
  'exit.terminal_stock_ratio': {
    label: 'registry.parameterKey.exit_terminal_stock_ratio.label',
    definition: 'registry.parameterKey.exit_terminal_stock_ratio.definition',
    formula: 'registry.parameterKey.exit_terminal_stock_ratio.formula',
  },
  'inseason.donor_minimum_weeks_of_supply': {
    label: 'registry.parameterKey.inseason_donor_minimum_weeks_of_supply.label',
    definition: 'registry.parameterKey.inseason_donor_minimum_weeks_of_supply.definition',
    formula: 'registry.parameterKey.inseason_donor_minimum_weeks_of_supply.formula',
  },
  'inseason.entry_point.node_role': {
    label: 'registry.parameterKey.inseason_entry_point_node_role.label',
    definition: 'registry.parameterKey.inseason_entry_point_node_role.definition',
    formula: 'registry.parameterKey.inseason_entry_point_node_role.formula',
  },
  'inseason.evidence.sales_history_weeks': {
    label: 'registry.parameterKey.inseason_evidence_sales_history_weeks.label',
    definition: 'registry.parameterKey.inseason_evidence_sales_history_weeks.definition',
    formula: 'registry.parameterKey.inseason_evidence_sales_history_weeks.formula',
  },
  'inseason.lifecycle.dead_ratio': {
    label: 'registry.parameterKey.inseason_lifecycle_dead_ratio.label',
    definition: 'registry.parameterKey.inseason_lifecycle_dead_ratio.definition',
    formula: 'registry.parameterKey.inseason_lifecycle_dead_ratio.formula',
  },
  'inseason.lifecycle.loser_ratio': {
    label: 'registry.parameterKey.inseason_lifecycle_loser_ratio.label',
    definition: 'registry.parameterKey.inseason_lifecycle_loser_ratio.definition',
    formula: 'registry.parameterKey.inseason_lifecycle_loser_ratio.formula',
  },
  'inseason.lifecycle.minimum_weeks': {
    label: 'registry.parameterKey.inseason_lifecycle_minimum_weeks.label',
    definition: 'registry.parameterKey.inseason_lifecycle_minimum_weeks.definition',
    formula: 'registry.parameterKey.inseason_lifecycle_minimum_weeks.formula',
  },
  'inseason.lifecycle.winner_ratio': {
    label: 'registry.parameterKey.inseason_lifecycle_winner_ratio.label',
    definition: 'registry.parameterKey.inseason_lifecycle_winner_ratio.definition',
    formula: 'registry.parameterKey.inseason_lifecycle_winner_ratio.formula',
  },
  'inseason.maximum_keepers': {
    label: 'registry.parameterKey.inseason_maximum_keepers.label',
    definition: 'registry.parameterKey.inseason_maximum_keepers.definition',
    formula: 'registry.parameterKey.inseason_maximum_keepers.formula',
  },
  'inseason.minimum_clearing_share': {
    label: 'registry.parameterKey.inseason_minimum_clearing_share.label',
    definition: 'registry.parameterKey.inseason_minimum_clearing_share.definition',
    formula: 'registry.parameterKey.inseason_minimum_clearing_share.formula',
  },
  'inseason.minimum_move_units': {
    label: 'registry.parameterKey.inseason_minimum_move_units.label',
    definition: 'registry.parameterKey.inseason_minimum_move_units.definition',
    formula: 'registry.parameterKey.inseason_minimum_move_units.formula',
  },
  'inseason.price_decision.governed_reason_codes': {
    label: 'registry.parameterKey.inseason_price_decision_governed_reason_codes.label',
    definition: 'registry.parameterKey.inseason_price_decision_governed_reason_codes.definition',
    formula: 'registry.parameterKey.inseason_price_decision_governed_reason_codes.formula',
  },
  'inseason.pull.absent_ranged_row_reads_zero': {
    label: 'registry.parameterKey.inseason_pull_absent_ranged_row_reads_zero.label',
    definition: 'registry.parameterKey.inseason_pull_absent_ranged_row_reads_zero.definition',
    formula: 'registry.parameterKey.inseason_pull_absent_ranged_row_reads_zero.formula',
  },
  'inseason.pull.accept_unmeasured_on_order': {
    label: 'registry.parameterKey.inseason_pull_accept_unmeasured_on_order.label',
    definition: 'registry.parameterKey.inseason_pull_accept_unmeasured_on_order.definition',
    formula: 'registry.parameterKey.inseason_pull_accept_unmeasured_on_order.formula',
  },
  'inseason.pull.accessories_in_pool': {
    label: 'registry.parameterKey.inseason_pull_accessories_in_pool.label',
    definition: 'registry.parameterKey.inseason_pull_accessories_in_pool.definition',
    formula: 'registry.parameterKey.inseason_pull_accessories_in_pool.formula',
  },
  // Transport cost per carton and pairs per carton (bata-platform ADR 6188): with the pallet and
  // shipment keys below, the cost a shipment must pay for after the replenishment solve.
  'inseason.pull.carton_cost': {
    label: 'registry.parameterKey.inseason_pull_carton_cost.label',
    definition: 'registry.parameterKey.inseason_pull_carton_cost.definition',
    formula: 'registry.parameterKey.inseason_pull_carton_cost.formula',
  },
  'inseason.pull.carton_units': {
    label: 'registry.parameterKey.inseason_pull_carton_units.label',
    definition: 'registry.parameterKey.inseason_pull_carton_units.definition',
    formula: 'registry.parameterKey.inseason_pull_carton_units.formula',
  },
  'inseason.pull.case_size_units': {
    label: 'registry.parameterKey.inseason_pull_case_size_units.label',
    definition: 'registry.parameterKey.inseason_pull_case_size_units.definition',
    formula: 'registry.parameterKey.inseason_pull_case_size_units.formula',
  },
  // ADR 0052 amendment (platform): the central demand — the mean, the mode (quantile, mean, hybrid) and the hybrid's threshold.
  'inseason.pull.central_demand_hybrid_threshold': {
    label: 'registry.parameterKey.inseason_pull_central_demand_hybrid_threshold.label',
    definition: 'registry.parameterKey.inseason_pull_central_demand_hybrid_threshold.definition',
    formula: 'registry.parameterKey.inseason_pull_central_demand_hybrid_threshold.formula',
  },
  'inseason.pull.central_demand_is_mean': {
    label: 'registry.parameterKey.inseason_pull_central_demand_is_mean.label',
    definition: 'registry.parameterKey.inseason_pull_central_demand_is_mean.definition',
    formula: 'registry.parameterKey.inseason_pull_central_demand_is_mean.formula',
  },
  'inseason.pull.central_demand_mode': {
    label: 'registry.parameterKey.inseason_pull_central_demand_mode.label',
    definition: 'registry.parameterKey.inseason_pull_central_demand_mode.definition',
    formula: 'registry.parameterKey.inseason_pull_central_demand_mode.formula',
  },
  'inseason.pull.central_level': {
    label: 'registry.parameterKey.inseason_pull_central_level.label',
    definition: 'registry.parameterKey.inseason_pull_central_level.definition',
    formula: 'registry.parameterKey.inseason_pull_central_level.formula',
  },
  'inseason.pull.consumable_size_codes': {
    label: 'registry.parameterKey.inseason_pull_consumable_size_codes.label',
    definition: 'registry.parameterKey.inseason_pull_consumable_size_codes.definition',
    formula: 'registry.parameterKey.inseason_pull_consumable_size_codes.formula',
  },
  // ADR 6205 (platform): the fallback forecast for in-stock sizes with none of their own, and its per-size cap.
  'inseason.pull.fallback_forecast': {
    label: 'registry.parameterKey.inseason_pull_fallback_forecast.label',
    definition: 'registry.parameterKey.inseason_pull_fallback_forecast.definition',
    formula: 'registry.parameterKey.inseason_pull_fallback_forecast.formula',
  },
  'inseason.pull.fallback_pairs_per_size': {
    label: 'registry.parameterKey.inseason_pull_fallback_pairs_per_size.label',
    definition: 'registry.parameterKey.inseason_pull_fallback_pairs_per_size.definition',
    formula: 'registry.parameterKey.inseason_pull_fallback_pairs_per_size.formula',
  },
  'inseason.pull.forecast_model_version': {
    label: 'registry.parameterKey.inseason_pull_forecast_model_version.label',
    definition: 'registry.parameterKey.inseason_pull_forecast_model_version.definition',
    formula: 'registry.parameterKey.inseason_pull_forecast_model_version.formula',
  },
  // bata-data's forecasts read it on each run's as-of date (platform, 2026-11-02).
  'inseason.pull.forecast_window_weeks': {
    label: 'registry.parameterKey.inseason_pull_forecast_window_weeks.label',
    definition: 'registry.parameterKey.inseason_pull_forecast_window_weeks.definition',
    formula: 'registry.parameterKey.inseason_pull_forecast_window_weeks.formula',
  },
  // The DEFAULT store lead time in whole days, read only for a store the master gives none (owner
  // ruling 2026-10-14, bata-platform ADR 6074 amendment).
  'inseason.pull.lead_time_days': {
    label: 'registry.parameterKey.inseason_pull_lead_time_days.label',
    definition: 'registry.parameterKey.inseason_pull_lead_time_days.definition',
    formula: 'registry.parameterKey.inseason_pull_lead_time_days.formula',
  },
  // RETIRED 2026-10-14: no longer read or published by the platform. Kept, labelled as retired, so a
  // run before the ruling that names it (what the run read, its governing keys) still reads in words.
  'inseason.pull.lead_time_periods': {
    label: 'registry.parameterKey.inseason_pull_lead_time_periods.label',
    definition: 'registry.parameterKey.inseason_pull_lead_time_periods.definition',
    formula: 'registry.parameterKey.inseason_pull_lead_time_periods.formula',
  },
  'inseason.pull.logistics_cost_per_unit': {
    label: 'registry.parameterKey.inseason_pull_logistics_cost_per_unit.label',
    definition: 'registry.parameterKey.inseason_pull_logistics_cost_per_unit.definition',
    formula: 'registry.parameterKey.inseason_pull_logistics_cost_per_unit.formula',
  },
  'inseason.pull.minimum_order_units': {
    label: 'registry.parameterKey.inseason_pull_minimum_order_units.label',
    definition: 'registry.parameterKey.inseason_pull_minimum_order_units.definition',
    formula: 'registry.parameterKey.inseason_pull_minimum_order_units.formula',
  },
  'inseason.pull.minimum_transport_units': {
    label: 'registry.parameterKey.inseason_pull_minimum_transport_units.label',
    definition: 'registry.parameterKey.inseason_pull_minimum_transport_units.definition',
    formula: 'registry.parameterKey.inseason_pull_minimum_transport_units.formula',
  },
  // ADR 6188 (platform): transport cost per pallet and pairs per pallet.
  'inseason.pull.pallet_cost': {
    label: 'registry.parameterKey.inseason_pull_pallet_cost.label',
    definition: 'registry.parameterKey.inseason_pull_pallet_cost.definition',
    formula: 'registry.parameterKey.inseason_pull_pallet_cost.formula',
  },
  'inseason.pull.pallet_units': {
    label: 'registry.parameterKey.inseason_pull_pallet_units.label',
    definition: 'registry.parameterKey.inseason_pull_pallet_units.definition',
    formula: 'registry.parameterKey.inseason_pull_pallet_units.formula',
  },
  'inseason.pull.presentation_minimum_units': {
    label: 'registry.parameterKey.inseason_pull_presentation_minimum_units.label',
    definition: 'registry.parameterKey.inseason_pull_presentation_minimum_units.definition',
    formula: 'registry.parameterKey.inseason_pull_presentation_minimum_units.formula',
  },
  'inseason.pull.proven_size_cover': {
    label: 'registry.parameterKey.inseason_pull_proven_size_cover.label',
    definition: 'registry.parameterKey.inseason_pull_proven_size_cover.definition',
    formula: 'registry.parameterKey.inseason_pull_proven_size_cover.formula',
  },
  'inseason.pull.proven_size_min_window_units': {
    label: 'registry.parameterKey.inseason_pull_proven_size_min_window_units.label',
    definition: 'registry.parameterKey.inseason_pull_proven_size_min_window_units.definition',
    formula: 'registry.parameterKey.inseason_pull_proven_size_min_window_units.formula',
  },
  'inseason.pull.ranged_cells_rise_limit': {
    label: 'registry.parameterKey.inseason_pull_ranged_cells_rise_limit.label',
    definition: 'registry.parameterKey.inseason_pull_ranged_cells_rise_limit.definition',
    formula: 'registry.parameterKey.inseason_pull_ranged_cells_rise_limit.formula',
  },
  'inseason.pull.ranging_recency_weeks': {
    label: 'registry.parameterKey.inseason_pull_ranging_recency_weeks.label',
    definition: 'registry.parameterKey.inseason_pull_ranging_recency_weeks.definition',
    formula: 'registry.parameterKey.inseason_pull_ranging_recency_weeks.formula',
  },
  // ADR 6025 amendment (platform): a size holding nothing that sold recently reads 0 on hand.
  'inseason.pull.recent_sales_weeks': {
    label: 'registry.parameterKey.inseason_pull_recent_sales_weeks.label',
    definition: 'registry.parameterKey.inseason_pull_recent_sales_weeks.definition',
    formula: 'registry.parameterKey.inseason_pull_recent_sales_weeks.formula',
  },
  'inseason.pull.review_periods': {
    label: 'registry.parameterKey.inseason_pull_review_periods.label',
    definition: 'registry.parameterKey.inseason_pull_review_periods.definition',
    formula: 'registry.parameterKey.inseason_pull_review_periods.formula',
  },
  'inseason.pull.service_target': {
    label: 'registry.parameterKey.inseason_pull_service_target.label',
    definition: 'registry.parameterKey.inseason_pull_service_target.definition',
    formula: 'registry.parameterKey.inseason_pull_service_target.formula',
  },
  // ADR 6188 (platform): the fixed cost of one shipment — one DC delivery to one store in one week.
  'inseason.pull.shipment_fixed_cost': {
    label: 'registry.parameterKey.inseason_pull_shipment_fixed_cost.label',
    definition: 'registry.parameterKey.inseason_pull_shipment_fixed_cost.definition',
    formula: 'registry.parameterKey.inseason_pull_shipment_fixed_cost.formula',
  },
  'inseason.pull.size_handout': {
    label: 'registry.parameterKey.inseason_pull_size_handout.label',
    definition: 'registry.parameterKey.inseason_pull_size_handout.definition',
    formula: 'registry.parameterKey.inseason_pull_size_handout.formula',
  },
  'inseason.pull.stationary_model_versions': {
    label: 'registry.parameterKey.inseason_pull_stationary_model_versions.label',
    definition: 'registry.parameterKey.inseason_pull_stationary_model_versions.definition',
    formula: 'registry.parameterKey.inseason_pull_stationary_model_versions.formula',
  },
  // ADR 6206 (platform), bata-reqs ADR 6001: the store × article forecast, shared to sizes by a curve.
  'inseason.pull.store_article_forecast': {
    label: 'registry.parameterKey.inseason_pull_store_article_forecast.label',
    definition: 'registry.parameterKey.inseason_pull_store_article_forecast.definition',
    formula: 'registry.parameterKey.inseason_pull_store_article_forecast.formula',
  },
  'inseason.pull.store_article_model_version': {
    label: 'registry.parameterKey.inseason_pull_store_article_model_version.label',
    definition: 'registry.parameterKey.inseason_pull_store_article_model_version.definition',
    formula: 'registry.parameterKey.inseason_pull_store_article_model_version.formula',
  },
  'inseason.reforecast.grain': {
    label: 'registry.parameterKey.inseason_reforecast_grain.label',
    definition: 'registry.parameterKey.inseason_reforecast_grain.definition',
    formula: 'registry.parameterKey.inseason_reforecast_grain.formula',
  },
  'inseason.reforecast.horizon_weeks': {
    label: 'registry.parameterKey.inseason_reforecast_horizon_weeks.label',
    definition: 'registry.parameterKey.inseason_reforecast_horizon_weeks.definition',
    formula: 'registry.parameterKey.inseason_reforecast_horizon_weeks.formula',
  },
  'inseason.reforecast.model_id': {
    label: 'registry.parameterKey.inseason_reforecast_model_id.label',
    definition: 'registry.parameterKey.inseason_reforecast_model_id.definition',
    formula: 'registry.parameterKey.inseason_reforecast_model_id.formula',
  },
  'inseason.reforecast.model_version_id': {
    label: 'registry.parameterKey.inseason_reforecast_model_version_id.label',
    definition: 'registry.parameterKey.inseason_reforecast_model_version_id.definition',
    formula: 'registry.parameterKey.inseason_reforecast_model_version_id.formula',
  },
  'inseason.run_damage_per_broken_size': {
    label: 'registry.parameterKey.inseason_run_damage_per_broken_size.label',
    definition: 'registry.parameterKey.inseason_run_damage_per_broken_size.definition',
    formula: 'registry.parameterKey.inseason_run_damage_per_broken_size.formula',
  },
  'inseason.simulation.cycle_window_minutes': {
    label: 'registry.parameterKey.inseason_simulation_cycle_window_minutes.label',
    definition: 'registry.parameterKey.inseason_simulation_cycle_window_minutes.definition',
    formula: 'registry.parameterKey.inseason_simulation_cycle_window_minutes.formula',
  },
  'inseason.write_back.cancellation_reason_vocabulary': {
    label: 'registry.parameterKey.inseason_write_back_cancellation_reason_vocabulary.label',
    definition: 'registry.parameterKey.inseason_write_back_cancellation_reason_vocabulary.definition',
    formula: 'registry.parameterKey.inseason_write_back_cancellation_reason_vocabulary.formula',
  },
  'markdown.candidate_queue_hold_weeks': {
    label: 'registry.parameterKey.markdown_candidate_queue_hold_weeks.label',
    definition: 'registry.parameterKey.markdown_candidate_queue_hold_weeks.definition',
    formula: 'registry.parameterKey.markdown_candidate_queue_hold_weeks.formula',
  },
  'markdown.currency_minor_unit': {
    label: 'registry.parameterKey.markdown_currency_minor_unit.label',
    definition: 'registry.parameterKey.markdown_currency_minor_unit.definition',
    formula: 'registry.parameterKey.markdown_currency_minor_unit.formula',
  },
  'markdown.deep_step_approver_role': {
    label: 'registry.parameterKey.markdown_deep_step_approver_role.label',
    definition: 'registry.parameterKey.markdown_deep_step_approver_role.definition',
    formula: 'registry.parameterKey.markdown_deep_step_approver_role.formula',
  },
  'markdown.list_price_quantile': {
    label: 'registry.parameterKey.markdown_list_price_quantile.label',
    definition: 'registry.parameterKey.markdown_list_price_quantile.definition',
    formula: 'registry.parameterKey.markdown_list_price_quantile.formula',
  },
  'markdown.maximum_single_step_depth': {
    label: 'registry.parameterKey.markdown_maximum_single_step_depth.label',
    definition: 'registry.parameterKey.markdown_maximum_single_step_depth.definition',
    formula: 'registry.parameterKey.markdown_maximum_single_step_depth.formula',
  },
  'markdown.maximum_single_step_rungs': {
    label: 'registry.parameterKey.markdown_maximum_single_step_rungs.label',
    definition: 'registry.parameterKey.markdown_maximum_single_step_rungs.definition',
    formula: 'registry.parameterKey.markdown_maximum_single_step_rungs.formula',
  },
  'markdown.minimum_weeks_between_steps': {
    label: 'registry.parameterKey.markdown_minimum_weeks_between_steps.label',
    definition: 'registry.parameterKey.markdown_minimum_weeks_between_steps.definition',
    formula: 'registry.parameterKey.markdown_minimum_weeks_between_steps.formula',
  },
  'markdown.minimum_months_between_steps': {
    label: 'registry.parameterKey.markdown_minimum_months_between_steps.label',
    definition: 'registry.parameterKey.markdown_minimum_months_between_steps.definition',
    formula: 'registry.parameterKey.markdown_minimum_months_between_steps.formula',
  },
  'markdown.minimum_months_on_sale': {
    label: 'registry.parameterKey.markdown_minimum_months_on_sale.label',
    definition: 'registry.parameterKey.markdown_minimum_months_on_sale.definition',
    formula: 'registry.parameterKey.markdown_minimum_months_on_sale.formula',
  },
  'markdown.minimum_weeks_on_sale': {
    label: 'registry.parameterKey.markdown_minimum_weeks_on_sale.label',
    definition: 'registry.parameterKey.markdown_minimum_weeks_on_sale.definition',
    formula: 'registry.parameterKey.markdown_minimum_weeks_on_sale.formula',
  },
  // bata-reqs issue 38 R4 (bata-platform ADR 6190): the shelf-price rounding of the Zona Sale and
  // outlet prices — the mode, the price points, the round-down cap.
  'markdown.price_rounding_mode': {
    label: 'registry.parameterKey.markdown_price_rounding_mode.label',
    definition: 'registry.parameterKey.markdown_price_rounding_mode.definition',
    formula: 'registry.parameterKey.markdown_price_rounding_mode.formula',
  },
  'markdown.price_point_rule': {
    label: 'registry.parameterKey.markdown_price_point_rule.label',
    definition: 'registry.parameterKey.markdown_price_point_rule.definition',
    formula: 'registry.parameterKey.markdown_price_point_rule.formula',
  },
  'markdown.price_point_max_extra_depth': {
    label: 'registry.parameterKey.markdown_price_point_max_extra_depth.label',
    definition: 'registry.parameterKey.markdown_price_point_max_extra_depth.definition',
    formula: 'registry.parameterKey.markdown_price_point_max_extra_depth.formula',
  },
  // bata-reqs issue 38, owner rulings 2026-10-17 second set (bata-platform ADR 6189 as amended).
  'markdown.ladder_expiry_warning_days': {
    label: 'registry.parameterKey.markdown_ladder_expiry_warning_days.label',
    definition: 'registry.parameterKey.markdown_ladder_expiry_warning_days.definition',
    formula: 'registry.parameterKey.markdown_ladder_expiry_warning_days.formula',
  },
  'market.timezone': {
    label: 'registry.parameterKey.market_timezone.label',
    definition: 'registry.parameterKey.market_timezone.definition',
    formula: 'registry.parameterKey.market_timezone.formula',
  },
  'markdown.price_rounding_quantum': {
    label: 'registry.parameterKey.markdown_price_rounding_quantum.label',
    definition: 'registry.parameterKey.markdown_price_rounding_quantum.definition',
    formula: 'registry.parameterKey.markdown_price_rounding_quantum.formula',
  },
  // bata-reqs issue 38 R2 (bata-platform ADR 2136, ADR 9068): how many days before a transition or
  // outlet window of the process calendar its recommendation runs.
  'process_calendar.transition.run_ahead_days': {
    label: 'registry.parameterKey.process_calendar_transition_run_ahead_days.label',
    definition: 'registry.parameterKey.process_calendar_transition_run_ahead_days.definition',
    formula: 'registry.parameterKey.process_calendar_transition_run_ahead_days.formula',
  },
  'process_calendar.outlet.run_ahead_days': {
    label: 'registry.parameterKey.process_calendar_outlet_run_ahead_days.label',
    definition: 'registry.parameterKey.process_calendar_outlet_run_ahead_days.definition',
    formula: 'registry.parameterKey.process_calendar_outlet_run_ahead_days.formula',
  },
  'markdown.zona_sales_max_months': {
    label: 'registry.parameterKey.markdown_zona_sales_max_months.label',
    definition: 'registry.parameterKey.markdown_zona_sales_max_months.definition',
    formula: 'registry.parameterKey.markdown_zona_sales_max_months.formula',
  },
  'markdown_test.terminal_stock_tolerance': {
    label: 'registry.parameterKey.markdown_test_terminal_stock_tolerance.label',
    definition: 'registry.parameterKey.markdown_test_terminal_stock_tolerance.definition',
    formula: 'registry.parameterKey.markdown_test_terminal_stock_tolerance.formula',
  },
  'markdown.terminal_residual_share': {
    label: 'registry.parameterKey.markdown_terminal_residual_share.label',
    definition: 'registry.parameterKey.markdown_terminal_residual_share.definition',
    formula: 'registry.parameterKey.markdown_terminal_residual_share.formula',
  },
  'markdown.trade_off.carrying_cost_rates': {
    label: 'registry.parameterKey.markdown_trade_off_carrying_cost_rates.label',
    definition: 'registry.parameterKey.markdown_trade_off_carrying_cost_rates.definition',
    formula: 'registry.parameterKey.markdown_trade_off_carrying_cost_rates.formula',
  },
  'module.exit_consolidation.cadence': {
    label: 'registry.parameterKey.module_exit_consolidation_cadence.label',
    definition: 'registry.parameterKey.module_exit_consolidation_cadence.definition',
    formula: 'registry.parameterKey.module_exit_consolidation_cadence.formula',
  },
  'module.exit_consolidation.enabled': {
    label: 'registry.parameterKey.module_exit_consolidation_enabled.label',
    definition: 'registry.parameterKey.module_exit_consolidation_enabled.definition',
    formula: 'registry.parameterKey.module_exit_consolidation_enabled.formula',
  },
  'module.exit_consolidation.excluded_doors': {
    label: 'registry.parameterKey.module_exit_consolidation_excluded_doors.label',
    definition: 'registry.parameterKey.module_exit_consolidation_excluded_doors.definition',
    formula: 'registry.parameterKey.module_exit_consolidation_excluded_doors.formula',
  },
  'module.inseason_price.cadence': {
    label: 'registry.parameterKey.module_inseason_price_cadence.label',
    definition: 'registry.parameterKey.module_inseason_price_cadence.definition',
    formula: 'registry.parameterKey.module_inseason_price_cadence.formula',
  },
  'module.inseason_price.enabled': {
    label: 'registry.parameterKey.module_inseason_price_enabled.label',
    definition: 'registry.parameterKey.module_inseason_price_enabled.definition',
    formula: 'registry.parameterKey.module_inseason_price_enabled.formula',
  },
  'module.inseason_replenishment.cadence': {
    label: 'registry.parameterKey.module_inseason_replenishment_cadence.label',
    definition: 'registry.parameterKey.module_inseason_replenishment_cadence.definition',
    formula: 'registry.parameterKey.module_inseason_replenishment_cadence.formula',
  },
  'module.inseason_replenishment.enabled': {
    label: 'registry.parameterKey.module_inseason_replenishment_enabled.label',
    definition: 'registry.parameterKey.module_inseason_replenishment_enabled.definition',
    formula: 'registry.parameterKey.module_inseason_replenishment_enabled.formula',
  },
  // RETIRED 2026-10-28 (bata-platform #881, ADR 9038 amended): run scopes replace it, and the catalogue no
  // longer declares it. Kept so an older run that names it still reads in words.
  'module.inseason_replenishment.excluded_doors': {
    label: 'registry.parameterKey.module_inseason_replenishment_excluded_doors.label',
    definition: 'registry.parameterKey.module_inseason_replenishment_excluded_doors.definition',
    formula: 'registry.parameterKey.module_inseason_replenishment_excluded_doors.formula',
  },
  'promotion_minimum_observations': {
    label: 'registry.parameterKey.promotion_minimum_observations.label',
    definition: 'registry.parameterKey.promotion_minimum_observations.definition',
    formula: 'registry.parameterKey.promotion_minimum_observations.formula',
  },
  'promotion_required_windows': {
    label: 'registry.parameterKey.promotion_required_windows.label',
    definition: 'registry.parameterKey.promotion_required_windows.definition',
    formula: 'registry.parameterKey.promotion_required_windows.formula',
  },
  'recovery_convergence_tolerance': {
    label: 'registry.parameterKey.recovery_convergence_tolerance.label',
    definition: 'registry.parameterKey.recovery_convergence_tolerance.definition',
    formula: 'registry.parameterKey.recovery_convergence_tolerance.formula',
  },
  'recovery_curve_min_product_units': {
    label: 'registry.parameterKey.recovery_curve_min_product_units.label',
    definition: 'registry.parameterKey.recovery_curve_min_product_units.definition',
    formula: 'registry.parameterKey.recovery_curve_min_product_units.formula',
  },
  'recovery_curve_min_product_weeks': {
    label: 'registry.parameterKey.recovery_curve_min_product_weeks.label',
    definition: 'registry.parameterKey.recovery_curve_min_product_weeks.definition',
    formula: 'registry.parameterKey.recovery_curve_min_product_weeks.formula',
  },
  'recovery_curve_min_store_units': {
    label: 'registry.parameterKey.recovery_curve_min_store_units.label',
    definition: 'registry.parameterKey.recovery_curve_min_store_units.definition',
    formula: 'registry.parameterKey.recovery_curve_min_store_units.formula',
  },
  'recovery_curve_min_store_weeks': {
    label: 'registry.parameterKey.recovery_curve_min_store_weeks.label',
    definition: 'registry.parameterKey.recovery_curve_min_store_weeks.definition',
    formula: 'registry.parameterKey.recovery_curve_min_store_weeks.formula',
  },
  'recovery_curve_product_weight': {
    label: 'registry.parameterKey.recovery_curve_product_weight.label',
    definition: 'registry.parameterKey.recovery_curve_product_weight.definition',
    formula: 'registry.parameterKey.recovery_curve_product_weight.formula',
  },
  'recovery_dispersion': {
    label: 'registry.parameterKey.recovery_dispersion.label',
    definition: 'registry.parameterKey.recovery_dispersion.definition',
    formula: 'registry.parameterKey.recovery_dispersion.formula',
  },
  'recovery_history_cap_multiple': {
    label: 'registry.parameterKey.recovery_history_cap_multiple.label',
    definition: 'registry.parameterKey.recovery_history_cap_multiple.definition',
    formula: 'registry.parameterKey.recovery_history_cap_multiple.formula',
  },
  'recovery_lower_interval_level': {
    label: 'registry.parameterKey.recovery_lower_interval_level.label',
    definition: 'registry.parameterKey.recovery_lower_interval_level.definition',
    formula: 'registry.parameterKey.recovery_lower_interval_level.formula',
  },
  'recovery_maximum_iterations': {
    label: 'registry.parameterKey.recovery_maximum_iterations.label',
    definition: 'registry.parameterKey.recovery_maximum_iterations.definition',
    formula: 'registry.parameterKey.recovery_maximum_iterations.formula',
  },
  'recovery_own_rate_min_exposure_weeks': {
    label: 'registry.parameterKey.recovery_own_rate_min_exposure_weeks.label',
    definition: 'registry.parameterKey.recovery_own_rate_min_exposure_weeks.definition',
    formula: 'registry.parameterKey.recovery_own_rate_min_exposure_weeks.formula',
  },
  'recovery_run_completeness_elasticity': {
    label: 'registry.parameterKey.recovery_run_completeness_elasticity.label',
    definition: 'registry.parameterKey.recovery_run_completeness_elasticity.definition',
    formula: 'registry.parameterKey.recovery_run_completeness_elasticity.formula',
  },
  'recovery_shrinkage_strength': {
    label: 'registry.parameterKey.recovery_shrinkage_strength.label',
    definition: 'registry.parameterKey.recovery_shrinkage_strength.definition',
    formula: 'registry.parameterKey.recovery_shrinkage_strength.formula',
  },
  'recovery_support_cap': {
    label: 'registry.parameterKey.recovery_support_cap.label',
    definition: 'registry.parameterKey.recovery_support_cap.definition',
    formula: 'registry.parameterKey.recovery_support_cap.formula',
  },
  'recovery_upper_interval_level': {
    label: 'registry.parameterKey.recovery_upper_interval_level.label',
    definition: 'registry.parameterKey.recovery_upper_interval_level.definition',
    formula: 'registry.parameterKey.recovery_upper_interval_level.formula',
  },
  'refresh_minimum_retained_share': {
    label: 'registry.parameterKey.refresh_minimum_retained_share.label',
    definition: 'registry.parameterKey.refresh_minimum_retained_share.definition',
    formula: 'registry.parameterKey.refresh_minimum_retained_share.formula',
  },
  'registry.expiry_warning_horizon_days': {
    label: 'registry.parameterKey.registry_expiry_warning_horizon_days.label',
    definition: 'registry.parameterKey.registry_expiry_warning_horizon_days.definition',
    formula: 'registry.parameterKey.registry_expiry_warning_horizon_days.formula',
  },
  'registry.product_scope_scheme': {
    label: 'registry.parameterKey.registry_product_scope_scheme.label',
    definition: 'registry.parameterKey.registry_product_scope_scheme.definition',
    formula: 'registry.parameterKey.registry_product_scope_scheme.formula',
  },
  'retention.acknowledged_event_days': {
    label: 'registry.parameterKey.retention_acknowledged_event_days.label',
    definition: 'registry.parameterKey.retention_acknowledged_event_days.definition',
    formula: 'registry.parameterKey.retention_acknowledged_event_days.formula',
  },
  'retention.assistant_turn_months': {
    label: 'registry.parameterKey.retention_assistant_turn_months.label',
    definition: 'registry.parameterKey.retention_assistant_turn_months.definition',
    formula: 'registry.parameterKey.retention_assistant_turn_months.formula',
  },
  // The days a simulation of the one move run is kept, read by the retention job (bata-platform #946,
  // ADR 9056 amended 2026-11-03). No row: the job skips compacting simulations by name.
  'retention.compacting_simulation_days': {
    label: 'registry.parameterKey.retention_compacting_simulation_days.label',
    definition: 'registry.parameterKey.retention_compacting_simulation_days.definition',
    formula: 'registry.parameterKey.retention_compacting_simulation_days.formula',
  },
  'retention.forecast_explanation_days': {
    label: 'registry.parameterKey.retention_forecast_explanation_days.label',
    definition: 'registry.parameterKey.retention_forecast_explanation_days.definition',
    formula: 'registry.parameterKey.retention_forecast_explanation_days.formula',
  },
  'retention.feedback_report_closed_months': {
    label: 'registry.parameterKey.retention_feedback_report_closed_months.label',
    definition: 'registry.parameterKey.retention_feedback_report_closed_months.definition',
    formula: 'registry.parameterKey.retention_feedback_report_closed_months.formula',
  },
  'retention.superseded_batch_days': {
    label: 'registry.parameterKey.retention_superseded_batch_days.label',
    definition: 'registry.parameterKey.retention_superseded_batch_days.definition',
    formula: 'registry.parameterKey.retention_superseded_batch_days.formula',
  },
  'review_half_sell_through_target': {
    label: 'registry.parameterKey.review_half_sell_through_target.label',
    definition: 'registry.parameterKey.review_half_sell_through_target.definition',
    formula: 'registry.parameterKey.review_half_sell_through_target.formula',
  },
  'stock_partition_max_age_days': {
    label: 'registry.parameterKey.stock_partition_max_age_days.label',
    definition: 'registry.parameterKey.stock_partition_max_age_days.definition',
    formula: 'registry.parameterKey.stock_partition_max_age_days.formula',
  },
  'store_weekly_move_cap': {
    label: 'registry.parameterKey.store_weekly_move_cap.label',
    definition: 'registry.parameterKey.store_weekly_move_cap.definition',
    formula: 'registry.parameterKey.store_weekly_move_cap.formula',
  },
  // bata-reqs ADR 9000 (owner ruling 2026-10-29): the pairs a store may send in a ship-week, beside the
  // move cap. The UI names it ahead of the platform catalogue, which declares it with the engine change.
  'store_weekly_outbound_units_cap': {
    label: 'registry.parameterKey.store_weekly_outbound_units_cap.label',
    definition: 'registry.parameterKey.store_weekly_outbound_units_cap.definition',
    formula: 'registry.parameterKey.store_weekly_outbound_units_cap.formula',
  },
  // bata-platform ADR 9058 (owner ruling 2026-11-03): the destination stores a store may send to in a
  // ship-week, decided by an optimisation over its articles, not a filter; and how its lanes are chosen.
  'store_weekly_outbound_destinations_cap': {
    label: 'registry.parameterKey.store_weekly_outbound_destinations_cap.label',
    definition: 'registry.parameterKey.store_weekly_outbound_destinations_cap.definition',
    formula: 'registry.parameterKey.store_weekly_outbound_destinations_cap.formula',
  },
  'inseason.outbound_lanes_mode': {
    label: 'registry.parameterKey.inseason_outbound_lanes_mode.label',
    definition: 'registry.parameterKey.inseason_outbound_lanes_mode.definition',
    formula: 'registry.parameterKey.inseason_outbound_lanes_mode.formula',
  },
  'inseason.outbound_lanes.relative_gap': {
    label: 'registry.parameterKey.inseason_outbound_lanes_relative_gap.label',
    definition: 'registry.parameterKey.inseason_outbound_lanes_relative_gap.definition',
    formula: 'registry.parameterKey.inseason_outbound_lanes_relative_gap.formula',
  },
  'inseason.outbound_lanes.time_limit_seconds': {
    label: 'registry.parameterKey.inseason_outbound_lanes_time_limit_seconds.label',
    definition: 'registry.parameterKey.inseason_outbound_lanes_time_limit_seconds.definition',
    formula: 'registry.parameterKey.inseason_outbound_lanes_time_limit_seconds.formula',
  },
  'supplier_on_time_tolerance_days': {
    label: 'registry.parameterKey.supplier_on_time_tolerance_days.label',
    definition: 'registry.parameterKey.supplier_on_time_tolerance_days.definition',
    formula: 'registry.parameterKey.supplier_on_time_tolerance_days.formula',
  },
  'supplier_in_full_tolerance_share': {
    label: 'registry.parameterKey.supplier_in_full_tolerance_share.label',
    definition: 'registry.parameterKey.supplier_in_full_tolerance_share.definition',
    formula: 'registry.parameterKey.supplier_in_full_tolerance_share.formula',
  },
  'supplier_lead_time_reference_days': {
    label: 'registry.parameterKey.supplier_lead_time_reference_days.label',
    definition: 'registry.parameterKey.supplier_lead_time_reference_days.definition',
    formula: 'registry.parameterKey.supplier_lead_time_reference_days.formula',
  },
  'vocabulary.label.': {
    label: 'registry.parameterKey.vocabulary_label_.label',
    definition: 'registry.parameterKey.vocabulary_label_.definition',
    formula: 'registry.parameterKey.vocabulary_label_.formula',
  },
};

/**
 * The exception rules' words, by rule id. The platform publishes no label for a rule
 * (`ExceptionRuleSummary` carries the id only), so these are this catalogue's: the three rules the
 * platform seeds (bata-platform ADR 2100, `services/repositories/exceptions.py`) and the three
 * earlier identifiers the queue still meets. A rule not named here is shown by its id.
 */
export const EXCEPTION_RULE_LABEL_KEY: Readonly<Record<string, string>> = {
  'inseason.cover_below': 'foundations.rule.inseasonCoverBelow',
  // The rule the register holds on the forecast-bias measure (`forecast_bias`, bata-platform
  // `planning/exception/bias.py`): named by its measure's words, as the bare identifier below is.
  'inseason.forecast_bias': 'foundations.rule.forecastBias',
  'inseason.size_run_broken': 'foundations.rule.inseasonSizeRunBroken',
  'cycle.failed_or_empty': 'foundations.rule.cycleFailedOrEmpty',
  coverage_breach: 'foundations.rule.coverageBreach',
  size_run_break: 'foundations.rule.sizeRunBreak',
  forecast_bias: 'foundations.rule.forecastBias',
};

/**
 * A rule's measure in words, shared by every chapter that names an alert (audit 4) — the three the platform's rules are seeded with (bata-platform
 * migration `f2100b8d4e63`, v0.61.0). A measure this table does not name is shown as its code.
 */
export const MEASURE_LABEL_KEY: Readonly<Record<string, string>> = {
  weeks_of_cover: 'foundations.measure.weeksOfCover',
  size_gap_stranded_units: 'foundations.measure.sizeGapStrandedUnits',
  cycle_tick_failed_or_empty: 'foundations.measure.cycleTickFailedOrEmpty',
  // The forecast-accuracy measures the accuracy warehouse publishes into the queue
  // (bata-platform `planning/exception/bias.py`, `ACCURACY_MEASURES`).
  forecast_bias: 'foundations.measure.forecastBias',
  forecast_wape: 'foundations.measure.forecastWape',
  forecast_mase: 'foundations.measure.forecastMase',
  forecast_tracking_signal: 'foundations.measure.forecastTrackingSignal',
  forecast_value_added: 'foundations.measure.forecastValueAdded',
};

/** The words for a measure, or `null` when this build names none. */
export function measureLabelKey(measure: string): string | null {
  return Object.prototype.hasOwnProperty.call(MEASURE_LABEL_KEY, measure) ? MEASURE_LABEL_KEY[measure] ?? null : null;
}

/**
 * The fields a rule governs under `exception.rule.<rule id>.<field>`: the four attributes a P4 set
 * may change (bata-platform `packages/planning/exception/governance.py`, `RULE_FIELDS`) and the two
 * per-market rows the parameter catalogue declares (`services/repositories/parameter_catalogue.py`:
 * `threshold`, `min_weekly_demand`).
 */
const RULE_FIELD_KEY: Readonly<Record<string, string>> = {
  is_active: 'planning.parameterName.ruleField.isActive',
  severity: 'planning.parameterName.ruleField.severity',
  sla_hours: 'planning.parameterName.ruleField.slaHours',
  perimeter: 'planning.parameterName.ruleField.perimeter',
  threshold: 'planning.parameterName.ruleField.threshold',
  min_weekly_demand: 'planning.parameterName.ruleField.minWeeklyDemand',
};

const RULE_KEY_PREFIX = 'exception.rule.';

/**
 * `{ ruleId, field }` for an exception rule's governed key, `null` for anything else. A rule id may
 * itself hold dots (`inseason.cover_below`), so the field is the last segment — the platform's own
 * `parse_attribute_key`. A field this build does not name is not parsed: the key stays the key. Pure.
 */
export function parseRuleKey(parameterKey: string): { readonly ruleId: string; readonly field: string } | null {
  if (parameterKey.includes('|') || !parameterKey.startsWith(RULE_KEY_PREFIX)) return null;
  const rest = parameterKey.slice(RULE_KEY_PREFIX.length);
  const cut = rest.lastIndexOf('.');
  if (cut <= 0) return null;
  const field = rest.slice(cut + 1);
  return RULE_FIELD_KEY[field] === undefined ? null : { ruleId: rest.slice(0, cut), field };
}

type NameTranslate = (key: string, values: Record<string, string>) => string;

/**
 * A registry key by its card's name where one is drafted, an exception rule's key by its rule and
 * field («Ciclo fallito o vuoto: attivazione»), else the key itself (usability audit A6): a planner
 * reads «Copertura minima delle taglie core», never `allocation.core_size_coverage`.
 */
export function parameterName(t: NameTranslate, parameterKey: string): string {
  const rule = parseRuleKey(parameterKey);
  // A governed rule key keeps its «rule: field» name even where a card is drafted for it (the card's
  // definition and formula still read on the register): the name says which rule and which field.
  const texts = rule === null ? parameterTexts(parameterKey) : null;
  if (texts !== null) return t(texts.label, {});
  if (rule === null) return parameterKey;
  const ruleKey = EXCEPTION_RULE_LABEL_KEY[rule.ruleId];
  return t('planning.parameterName.ruleAttribute', {
    rule: ruleKey === undefined ? rule.ruleId : t(ruleKey, {}),
    attribute: t(RULE_FIELD_KEY[rule.field] ?? rule.field, {}),
  });
}

/** Whether `parameterName` names the key in words rather than showing the key itself. Pure. */
export function hasParameterName(parameterKey: string): boolean {
  return parameterTexts(parameterKey) !== null || parseRuleKey(parameterKey) !== null;
}

/** The prose keys of a registry key, or `null` when no card text has been drafted for it. */
export function parameterTexts(parameterKey: string): ParameterTextKeys | null {
  return PARAMETER_TEXTS[parameterKey] ?? null;
}
